import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { OrgMember } from '../services/organizationService';
import TaskDescriptionEditor from './TaskDescriptionEditor';
import { useAuth } from '../contexts/AuthContext';
import { useActiveProject } from '../hooks/useActiveProject';
import { insightService } from '../services/insightService';
import '../styles/CustomerModal.css';
import '../styles/InsightModal.css';
import '../styles/PrioritizeModal.css';

/* ── Types (mirror Prioritize.tsx; duplicated here to keep the
   component self-contained and easy to read) ──────────────── */
type TabKey = 'description' | 'insights' | 'health' | 'release-notes' | 'history';

export type ItemKind = 'product' | 'epic' | 'feature' | 'initiative' | 'release' | 'objective' | 'release-group';
export type HealthState = 'on-track' | 'at-risk' | 'off-track' | 'none';
export type FeatureStatus = 'new-idea' | 'candidate' | 'planned' | 'in-progress' | 'released' | 'backlog' | 'done' | 'upcoming' | 'completed';

export const FEATURE_STATUS_LABEL: Record<FeatureStatus, string> = {
  'new-idea':    'New idea',
  'candidate':   'Candidate',
  'planned':     'Planned',
  'in-progress': 'In progress',
  'released':    'Released',
  'backlog':     'Backlog',
  'done':        'Done',
  'upcoming':    'Upcoming',
  'completed':   'Completed',
};

/* Release statuses (3-option, flag icons) — separate vocabulary from features. */
export const RELEASE_STATUS_OPTIONS: { value: FeatureStatus; label: string; color: string }[] = [
  { value: 'upcoming',    label: 'Upcoming',    color: '#3b82f6' },
  { value: 'in-progress', label: 'In Progress', color: '#f59e0b' },
  { value: 'completed',   label: 'Completed',   color: '#22c55e' },
];
export type TShirtSize = 'XS' | 'S' | 'M' | 'L' | 'XL';
export type BusinessValueLevel = 'low' | 'medium' | 'high';
export type ValueGauge = 1 | 2 | 3 | 4 | 5;

export interface PrioritizeItem {
  id: string;
  kind: ItemKind;
  name: string;
  description?: string;
  objective?: string | null;
  ownerId?: string | null;
  importanceScore?: number;
  color?: string;
  health?: HealthState;
  status?: FeatureStatus | null;
  startDate?: string;
  endDate?: string;
  parentId?: string | null;          // component → product, feature → component
  productParentId?: string | null;   // feature → product (alternate link)
  initiativeParentId?: string | null; // sub-item → initiative
  releaseParentId?: string | null;    // feature → release
  objectiveParentId?: string | null;  // feature → objective
  releaseGroupParentId?: string | null; // release → parent release (group)
  /* Feature linked-section arrays (stored in JSONB; see the feature popup). */
  dependencyIds?: string[];   // other features this feature depends on
  companyIds?: string[];      // linked companies (customers module)
  userIds?: string[];         // linked customer users (customers module)
  documentIds?: string[];     // linked documents (docs writer)
  createdAt: string;
  /* ── New sidebar fields (Insights-style) ── */
  teams?: string | null;
  sampleTag?: string | null;
  effort?: number | null;
  value?: ValueGauge | null;
  businessValueLevel?: BusinessValueLevel | null;
  businessValueScore?: number | null;
  stackRank?: number | null;
  tshirtSize?: TShirtSize | null;
  designAssets?: string | null;
  designerId?: string | null;
  development?: string | null;
  launchReady?: string | boolean | null;
  productSpec?: string | null;
  releaseNotes?: string | null;
  techLeadId?: string | null;
  workProgress?: number | null;
  workProgressMode?: 'auto' | 'manual' | null; // release work-progress source
  /* Tab content */
  insights?: Array<{ id: string; title?: string; companyName: string; noteType: string; importance?: ValueGauge | null; note: string; createdAt: string; insightId?: string | null }>;
  healthUpdates?: Array<{ status: string; label: string; note?: string; by: string; at: string }>;
  releaseNoteEntries?: Array<{ id: string; status: string; label: string; note: string; by: string; at: string }>;
  comments?: Array<{ id: string; author: string; text: string; at: string; reactions?: Record<string, number> }>;
  attachments?: Array<{ id: string; name: string; type: string; url: string }>;
}

/* Lightweight shapes for the feature popup's external link pickers. */
export type LinkCompany = { id: string; name: string };
export type LinkUser = { id: string; name: string; email?: string | null };
export type LinkDocument = { id: string; title: string };

export type InsightEntry = { id: string; title?: string; companyName: string; noteType: string; importance?: ValueGauge | null; note: string; createdAt: string; insightId?: string | null };
export type ReleaseNoteEntry = { id: string; status: string; label: string; note: string; by: string; at: string };
export type HealthEntry = { status: string; label: string; note?: string; by: string; at: string };

interface PrioritizeDetailModalProps {
  item: PrioritizeItem | null;
  /** siblings used for "Hierarchy parent" picker (only items of a *valid*
      kind — products for components/components for features/products for
      features, depending on what this item is) */
  parentCandidates: PrioritizeItem[];
  members: OrgMember[];
  /** Full item list — used by the release popup for linked-feature counts
      and the parent-release-group picker. Optional (other pages omit it). */
  allItems?: PrioritizeItem[];
  /** Create a release inside a release group from the group popup's Releases
      section. The page persists it and refreshes its lists. Optional. */
  onCreateRelease?: (name: string, groupId: string) => void;
  /** Delete this item (used by the release popup's menu). Optional. */
  onDelete?: (id: string) => void;
  /** Link/unlink a feature to this release (release popup Features "+"). Optional. */
  onToggleFeature?: (featureId: string, checked: boolean) => void;
  /** External data sources for the feature popup's linked-section pickers. Optional. */
  companies?: LinkCompany[];
  customerUsers?: LinkUser[];
  documents?: LinkDocument[];
  onClose: () => void;
  onSave: (item: PrioritizeItem) => void;
}

/* ── Static reference symbols (used in icon + legend) ──────── */
export const ReleaseGroupSymbol: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <rect x="8" y="3" width="13" height="13" rx="2" />
    <path d="M16 16v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h3" />
  </svg>
);
export const ProductSymbol: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = '#3B82F6' }) => (
  <span
    className="prioritize-symbol-product"
    style={{ width: size, height: size, background: color }}
    title="Product"
  />
);
export const ComponentSymbol: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = '#8B5CF6' }) => (
  <span
    className="prioritize-symbol-component"
    style={{ width: size, height: size, background: color }}
    title="Component"
  >
    <span style={{ background: 'rgba(255,255,255,0.85)' }} />
    <span style={{ background: 'rgba(255,255,255,0.85)' }} />
    <span style={{ background: 'rgba(255,255,255,0.85)' }} />
    <span style={{ background: 'rgba(255,255,255,0.85)' }} />
  </span>
);
export const FeatureSymbol: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'var(--brand-500)' }) => (
  <span
    className="prioritize-symbol-feature"
    style={{ width: size, height: size, background: color }}
    title="Feature"
  />
);

/* ── Field-row icon set (the icons in the sidebar match the
   reference image: briefcase, people, tag, clock, gauge, hash,
   checkmark-in-circle, person, etc.) ──────────────────────── */
const FIcon: React.FC<{ name: string; size?: number; color?: string }> = ({ name, size = 14, color = '#6b7280' }) => {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'hierarchy':
      return <svg {...props}><path d="M3 6h6M9 6v6h6M15 12v6M3 6a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM9 6a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM15 6a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM9 12a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM15 18a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"/></svg>;
    case 'bookmark':
      return <svg {...props}><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>;
    case 'briefcase':
      return <svg {...props}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>;
    case 'calendar':
      return <svg {...props}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
    case 'people':
      return <svg {...props}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>;
    case 'tag':
      return <svg {...props}><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;
    case 'clock':
      return <svg {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
    case 'gauge':
      return <svg {...props}><path d="M12 14l4-4"/><path d="M3.34 19a10 10 0 1117.32 0"/></svg>;
    case 'check-circle':
      return <svg {...props}><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>;
    case 'person':
      return <svg {...props}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
    case 'hash':
      return <svg {...props}><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>;
    case 'checkbox':
      return <svg {...props}><rect x="3" y="3" width="18" height="18" rx="2"/><polyline points="9 12 11 14 15 10"/></svg>;
    case 'tshirt':
      return <svg {...props}><path d="M20.38 3.46L16 2a4 4 0 01-8 0L3.62 3.46a2 2 0 00-1.34 2.23l.58 3.47a1 1 0 00.99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 002-2V10h2.15a1 1 0 00.99-.84l.58-3.47a2 2 0 00-1.34-2.23z"/></svg>;
    case 'lines':
      return <svg {...props}><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>;
    default: return null;
  }
};

/* Decide whether a dropdown should open upward: if the trigger sits low
   in the viewport (not enough room below for ~240px of menu), flip up.
   Returns [dropUp, recompute] — call recompute when opening. */
const useDropUp = (open: boolean) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [dropUp, setDropUp] = useState(false);
  useEffect(() => {
    if (!open || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setDropUp(spaceBelow < 248 && spaceAbove > spaceBelow);
  }, [open]);
  return { wrapRef, dropUp };
};

/* ── Sub-components for individual field rows ──────────────── */
const FieldRow: React.FC<{ icon: string; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div className="form-group-sidebar-new">
    <label className="sidebar-label-new">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#64748b' }}>
        <FIcon name={icon} size={11} />
        <span>{label}</span>
      </span>
    </label>
    <div className="prm-field-value">{children}</div>
  </div>
);

const NotAssigned: React.FC = () => <span className="prm-not-assigned">Not assigned</span>;

/* Relative timestamp ("a few seconds ago", "3 min ago", …). */
const relativeTime = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 10) return 'a few seconds ago';
  if (s < 60) return `${s} seconds ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m > 1 ? 's' : ''} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d > 1 ? 's' : ''} ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const QUICK_REACTIONS = ['👍', '❤️', '🎉', '😄', '🚀', '👀'];

type PrmComment = { id: string; author: string; text: string; at: string; reactions?: Record<string, number> };

/* Comment card with reaction / Edit / Delete / timestamp + reactions row,
   matching the reference design. */
const CommentCard: React.FC<{
  comment: PrmComment;
  onEdit: (id: string, text: string) => void;
  onDelete: (id: string) => void;
  onReact: (id: string, emoji: string) => void;
}> = ({ comment, onEdit, onDelete, onReact }) => {
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(comment.text);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pickerOpen) return;
    const h = (e: MouseEvent) => { if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [pickerOpen]);

  const reactions = Object.entries(comment.reactions ?? {}).filter(([, n]) => n > 0);

  return (
    <div className="prm-comment-card">
      <div className="prm-comment-head">
        <div className="prm-comment-author-group">
          <span className="prm-comment-avatar">{comment.author?.charAt(0)?.toUpperCase() || 'U'}</span>
          <span className="prm-comment-author">{comment.author}</span>
        </div>
        <div className="prm-comment-actions">
          <div className="prm-comment-react-wrap" ref={pickerRef}>
            <button type="button" className="prm-comment-action-btn" title="Add reaction" onClick={() => setPickerOpen(o => !o)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
            </button>
            {pickerOpen && (
              <div className="prm-reaction-picker">
                {QUICK_REACTIONS.map(emoji => (
                  <button key={emoji} type="button" className="prm-reaction-pick" onClick={() => { onReact(comment.id, emoji); setPickerOpen(false); }}>{emoji}</button>
                ))}
              </div>
            )}
          </div>
          <button type="button" className="prm-comment-action-link" onClick={() => { setEditing(true); setDraftText(comment.text); }}>Edit</button>
          <button type="button" className="prm-comment-action-link" onClick={() => onDelete(comment.id)}>Delete</button>
          <span className="prm-comment-time">{relativeTime(comment.at)}</span>
        </div>
      </div>

      {editing ? (
        <div className="prm-comment-edit">
          <textarea
            className="prm-comment-edit-input"
            value={draftText}
            onChange={e => setDraftText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); if (draftText.trim()) { onEdit(comment.id, draftText.trim()); setEditing(false); } }
              if (e.key === 'Escape') setEditing(false);
            }}
            rows={3}
            autoFocus
          />
          <div className="prm-comment-edit-actions">
            <button
              type="button"
              className="prm-comment-edit-save"
              disabled={!draftText.trim()}
              onClick={() => { if (draftText.trim()) { onEdit(comment.id, draftText.trim()); setEditing(false); } }}
            >
              Save
            </button>
            <button type="button" className="prm-comment-edit-cancel" onClick={() => { setDraftText(comment.text); setEditing(false); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="prm-comment-body">{comment.text}</div>
      )}

      <div className="prm-comment-reactions">
        {reactions.map(([emoji, n]) => (
          <button key={emoji} type="button" className="prm-reaction-chip prm-reaction-chip-on" onClick={() => onReact(comment.id, emoji)}>
            <span>{emoji}</span><span className="prm-reaction-count">{n}</span>
          </button>
        ))}
        <button type="button" className="prm-reaction-add" title="Add reaction" onClick={() => setPickerOpen(true)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </button>
      </div>
    </div>
  );
};

const StatusValue: React.FC<{ status?: FeatureStatus | null }> = ({ status }) => {
  if (!status) return <NotAssigned />;
  const colorMap: Record<FeatureStatus, { bg: string; fg: string }> = {
    'in-progress': { bg: '#ECEAFF', fg: '#432EA3' },
    'new-idea':    { bg: '#ccfbf1', fg: '#115e59' },
    'candidate':   { bg: '#f3e8ff', fg: '#6b21a8' },
    'planned':     { bg: '#dbeafe', fg: '#1e40af' },
    'released':    { bg: '#d1fae5', fg: '#047857' },
    'backlog':     { bg: '#f3f4f6', fg: '#4b5563' },
    'done':        { bg: '#d1fae5', fg: '#047857' },
    'upcoming':    { bg: '#dbeafe', fg: '#1e40af' },
    'completed':   { bg: '#d1fae5', fg: '#047857' },
  };
  const c = colorMap[status];
  return (
    <span className="prm-status-pill" style={{ background: c.bg, color: c.fg }}>
      <FIcon name="bookmark" size={11} color={c.fg} />
      {FEATURE_STATUS_LABEL[status]}
    </span>
  );
};

const TimeframeValue: React.FC<{ start?: string; end?: string }> = ({ start, end }) => {
  if (!start && !end) return <NotAssigned />;
  const fmt = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  return <span style={{ color: '#111827', fontSize: 12 }}>{fmt(start) || '—'} – {fmt(end) || '—'}</span>;
};

const EffortValue: React.FC<{ effort?: number | null }> = ({ effort }) => {
  if (effort == null) return <NotAssigned />;
  return (
    <span className="prm-effort">
      <FIcon name="person" size={12} color="#6b7280" />
      <strong>{effort}</strong>
    </span>
  );
};

/* 5-level graduated gauge. Selecting level N fills boxes 1..N in
   progressively more saturated blue (signal-strength style). Clicking
   the current level again clears it. */
const ValueGaugeControl: React.FC<{ value?: ValueGauge | null; onChange: (v: ValueGauge | null) => void }> = ({ value, onChange }) => {
  const titles = ['Very low', 'Low', 'Medium', 'High', 'Very high'];
  return (
    <span className="prm-gauge">
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          className={`prm-gauge-box ${value && i <= value ? `prm-gauge-box-fill prm-gauge-box-fill-${i}` : ''}`}
          onClick={() => onChange(value === i ? null : (i as ValueGauge))}
          title={titles[i - 1]}
        />
      ))}
    </span>
  );
};

/* Colored "select-box" icon used in the Business Value / Stack-rank
   options (a small rounded square with a chevron, tinted per option). */
const SelectBoxIcon: React.FC<{ color: string }> = ({ color }) => (
  <span className="prm-selectbox-icon" style={{ color }}>
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <polyline points="8 11 12 15 16 11" />
    </svg>
  </span>
);

const BV_OPTIONS: { value: BusinessValueLevel; label: string; color: string }[] = [
  { value: 'low',    label: 'Low',    color: '#3B82F6' },  // blue
  { value: 'medium', label: 'Medium', color: '#94A3B8' },  // gray
  { value: 'high',   label: 'High',   color: '#22C55E' },  // green
];

const BusinessValueSelect: React.FC<{ value?: BusinessValueLevel | null; onChange: (v: BusinessValueLevel | null) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const { wrapRef, dropUp } = useDropUp(open);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, wrapRef]);
  const selected = BV_OPTIONS.find(o => o.value === value);
  return (
    <div className="prm-input-wrap" ref={wrapRef}>
      <button type="button" className="prm-trigger" onClick={() => setOpen(o => !o)}>
        {selected ? (
          <span className="prm-selectbox-val"><SelectBoxIcon color={selected.color} /><span>{selected.label}</span></span>
        ) : (
          <span className="prm-trigger-placeholder">Not assigned</span>
        )}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className={`prm-dropdown ${dropUp ? 'prm-dropdown-up' : ''}`}>
          {BV_OPTIONS.map(o => (
            <button key={o.value} type="button" className={`prm-dropdown-item ${o.value === value ? 'prm-dropdown-item-selected' : ''}`} onClick={() => { onChange(o.value); setOpen(false); }}>
              <SelectBoxIcon color={o.color} />
              <span>{o.label}</span>
            </button>
          ))}
          {value && (
            <>
              <div className="prm-dropdown-divider" />
              <button type="button" className="prm-dropdown-item prm-dropdown-item-muted" onClick={() => { onChange(null); setOpen(false); }}>
                <span>Clear</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const RANK_OPTIONS: { value: number; color: string }[] = [
  { value: 3, color: '#3B82F6' },  // blue
  { value: 2, color: '#94A3B8' },  // gray
  { value: 1, color: '#22C55E' },  // green
];

const StackRankSelect: React.FC<{ rank?: number | null; onChange: (n: number | null) => void }> = ({ rank, onChange }) => {
  const [open, setOpen] = useState(false);
  const { wrapRef, dropUp } = useDropUp(open);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, wrapRef]);
  const selected = RANK_OPTIONS.find(o => o.value === rank);
  return (
    <div className="prm-input-wrap" ref={wrapRef}>
      <button type="button" className="prm-trigger" onClick={() => setOpen(o => !o)}>
        {selected ? (
          <span className="prm-selectbox-val"><SelectBoxIcon color={selected.color} /><span>{selected.value}</span></span>
        ) : (
          <span className="prm-trigger-placeholder">Not assigned</span>
        )}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className={`prm-dropdown ${dropUp ? 'prm-dropdown-up' : ''}`}>
          {RANK_OPTIONS.map(o => (
            <button key={o.value} type="button" className={`prm-dropdown-item ${o.value === rank ? 'prm-dropdown-item-selected' : ''}`} onClick={() => { onChange(o.value); setOpen(false); }}>
              <SelectBoxIcon color={o.color} />
              <span>{o.value}</span>
            </button>
          ))}
          {rank != null && (
            <>
              <div className="prm-dropdown-divider" />
              <button type="button" className="prm-dropdown-item prm-dropdown-item-muted" onClick={() => { onChange(null); setOpen(false); }}>
                <span>Clear</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* T-Shirt sizing select. Each option shows N green shirts + the size
   label, per the design (XS=1 … L=4). */
const TSHIRT_OPTIONS: { value: TShirtSize; count: number }[] = [
  { value: 'XS', count: 1 },
  { value: 'S',  count: 2 },
  { value: 'M',  count: 3 },
  { value: 'L',  count: 4 },
];
const TShirtOptionValue: React.FC<{ size: TShirtSize; count: number }> = ({ size, count }) => (
  <span className="prm-selectbox-val">
    <SelectBoxIcon color="#94A3B8" />
    <span className="prm-tshirt-icons">
      {Array.from({ length: count }).map((_, i) => <FIcon key={i} name="tshirt" size={13} color="#22C55E" />)}
    </span>
    <span className="prm-tshirt-label">({size})</span>
  </span>
);
const TShirtSelect: React.FC<{ size?: TShirtSize | null; onChange: (s: TShirtSize | null) => void }> = ({ size, onChange }) => {
  const [open, setOpen] = useState(false);
  const { wrapRef, dropUp } = useDropUp(open);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, wrapRef]);
  const selected = TSHIRT_OPTIONS.find(o => o.value === size);
  return (
    <div className="prm-input-wrap" ref={wrapRef}>
      <button type="button" className="prm-trigger" onClick={() => setOpen(o => !o)}>
        {selected
          ? <TShirtOptionValue size={selected.value} count={selected.count} />
          : <span className="prm-trigger-placeholder">Not assigned</span>}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className={`prm-dropdown ${dropUp ? 'prm-dropdown-up' : ''}`}>
          {TSHIRT_OPTIONS.map(o => (
            <button key={o.value} type="button" className={`prm-dropdown-item ${o.value === size ? 'prm-dropdown-item-selected' : ''}`} onClick={() => { onChange(o.value); setOpen(false); }}>
              <TShirtOptionValue size={o.value} count={o.count} />
            </button>
          ))}
          {size && (
            <>
              <div className="prm-dropdown-divider" />
              <button type="button" className="prm-dropdown-item prm-dropdown-item-muted" onClick={() => { onChange(null); setOpen(false); }}>
                <span>Clear</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* Colored circle-check icon (filled circle + white tick) used by the
   status-style selects (Design Assets / Development / Product Spec /
   Launch Ready). */
const CircleCheckIcon: React.FC<{ color: string }> = ({ color }) => (
  <span className="prm-circlecheck-icon" style={{ background: color }}>
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
  </span>
);

/* Generic icon-status select — driven by an options array.
   Stores the option `value` string on the field. */
const IconStatusSelect: React.FC<{
  value?: string | boolean | null;
  options: { value: string; label: string; color: string }[];
  onChange: (v: string | null) => void;
}> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const { wrapRef, dropUp } = useDropUp(open);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, wrapRef]);
  const selected = options.find(o => o.value === value);
  return (
    <div className="prm-input-wrap" ref={wrapRef}>
      <button type="button" className="prm-trigger" onClick={() => setOpen(o => !o)}>
        {selected ? (
          <span className="prm-selectbox-val"><CircleCheckIcon color={selected.color} /><span>{selected.label}</span></span>
        ) : (
          <span className="prm-trigger-placeholder">Not assigned</span>
        )}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className={`prm-dropdown ${dropUp ? 'prm-dropdown-up' : ''}`}>
          {options.map(o => (
            <button key={o.value} type="button" className={`prm-dropdown-item ${o.value === value ? 'prm-dropdown-item-selected' : ''}`} onClick={() => { onChange(o.value); setOpen(false); }}>
              <CircleCheckIcon color={o.color} />
              <span>{o.label}</span>
            </button>
          ))}
          {value && (
            <>
              <div className="prm-dropdown-divider" />
              <button type="button" className="prm-dropdown-item prm-dropdown-item-muted" onClick={() => { onChange(null); setOpen(false); }}>
                <span>Clear</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* Option sets from the design images. */
const STATUS5_OPTIONS = [
  { value: 'not-needed',  label: 'Not needed',  color: '#94A3B8' },  // gray
  { value: 'planned',     label: 'Planned',     color: '#3B82F6' },  // blue
  { value: 'blocked',     label: 'Blocked',     color: '#EF4444' },  // red
  { value: 'in-progress', label: 'In progress', color: '#F59E0B' },  // yellow/amber
  { value: 'completed',   label: 'Completed',   color: '#22C55E' },  // green
];
const LAUNCH_OPTIONS = [
  { value: 'delayed',  label: 'Delayed',  color: '#F97316' },  // orange
  { value: 'on-track', label: 'On track', color: '#22C55E' },  // green
];

/* ── Main component ───────────────────────────────────────── */
const PrioritizeDetailModal: React.FC<PrioritizeDetailModalProps> = ({ item, parentCandidates, members, allItems, onCreateRelease, onDelete, onToggleFeature, companies, customerUsers, documents, onClose, onSave }) => {
  const { user } = useAuth();
  const { activeProjectId } = useActiveProject();
  const [draft, setDraft] = useState<PrioritizeItem | null>(item);
  const [activeTab, setActiveTab] = useState<TabKey>('description');
  const [relInlineOpen, setRelInlineOpen] = useState(false);
  const [relInlineName, setRelInlineName] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [wpMenuOpen, setWpMenuOpen] = useState(false);
  const wpMenuRef = useRef<HTMLDivElement>(null);
  const [featPickerOpen, setFeatPickerOpen] = useState(false);
  const [featPickerQuery, setFeatPickerQuery] = useState('');
  const featPickerRef = useRef<HTMLDivElement>(null);
  // Feature linked-section pickers (dependencies/objectives/releases/companies/users/documents)
  const [linkPicker, setLinkPicker] = useState<string | null>(null);
  const [linkQuery, setLinkQuery] = useState('');
  const linkPickerRef = useRef<HTMLDivElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [healthUpdateModalOpen, setHealthUpdateModalOpen] = useState(false);
  const [editingHealthEntry, setEditingHealthEntry] = useState<HealthEntry | null>(null);
  const [addInsightOpen, setAddInsightOpen] = useState(false);
  const [editingInsight, setEditingInsight] = useState<InsightEntry | null>(null);
  const [releaseNoteOpen, setReleaseNoteOpen] = useState(false);
  const [editingReleaseNote, setEditingReleaseNote] = useState<ReleaseNoteEntry | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [commentInput, setCommentInput] = useState('');
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toggleSection = (key: string) => setOpenSections(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  useEffect(() => { setDraft(item); setActiveTab('description'); setCommentInput(''); }, [item?.id]);

  // Close the header "more options" menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const h = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showMenu]);

  // Close the work-progress gear menu on outside click
  useEffect(() => {
    if (!wpMenuOpen) return;
    const h = (e: MouseEvent) => { if (wpMenuRef.current && !wpMenuRef.current.contains(e.target as Node)) setWpMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [wpMenuOpen]);

  // Close the in-modal feature picker on outside click
  useEffect(() => {
    if (!featPickerOpen) return;
    const h = (e: MouseEvent) => { if (featPickerRef.current && !featPickerRef.current.contains(e.target as Node)) setFeatPickerOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [featPickerOpen]);

  // Close the feature linked-section picker on outside click.
  useEffect(() => {
    if (!linkPicker) return;
    const h = (e: MouseEvent) => { if (linkPickerRef.current && !linkPickerRef.current.contains(e.target as Node)) setLinkPicker(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [linkPicker]);

  const openHealthUpdateModal = (entry?: HealthEntry) => {
    setEditingHealthEntry(entry || null);
    setHealthUpdateModalOpen(true);
  };
  const deleteHealthEntry = (target: HealthEntry) => {
    setDraft(d => (d ? { ...d, healthUpdates: (d.healthUpdates ?? []).filter(h => h !== target) } : d));
  };
  const deleteReleaseNote = (id: string) => {
    setDraft(d => (d ? { ...d, releaseNoteEntries: (d.releaseNoteEntries ?? []).filter(r => r.id !== id) } : d));
  };
  const deleteInsight = (id: string) => {
    const target = (draft?.insights ?? []).find(i => i.id === id);
    if (target?.insightId) {
      insightService.deleteInsight(target.insightId).catch(err => console.error('Failed to delete insight from Insights page:', err));
    }
    setDraft(d => (d ? { ...d, insights: (d.insights ?? []).filter(i => i.id !== id) } : d));
  };

  const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

  // Local attachments (preview only — held as data URLs on the draft).
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!files.length) return;
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const att = { id: uid(), name: file.name, type: file.type, url: String(reader.result) };
        setDraft(d => (d ? { ...d, attachments: [...(d.attachments ?? []), att] } : d));
      };
      reader.readAsDataURL(file);
    });
  };
  const removeAttachment = (id: string) => {
    setDraft(d => (d ? { ...d, attachments: (d.attachments ?? []).filter(a => a.id !== id) } : d));
  };

  // Local comments (in-memory on the draft).
  const addComment = () => {
    const text = commentInput.trim();
    if (!text) return;
    const author = user?.user_metadata?.full_name || user?.email || 'Me';
    const c = { id: uid(), author, text, at: new Date().toISOString(), reactions: {} as Record<string, number> };
    setDraft(d => (d ? { ...d, comments: [...(d.comments ?? []), c] } : d));
    setCommentInput('');
  };
  const editComment = (id: string, text: string) => {
    setDraft(d => (d ? { ...d, comments: (d.comments ?? []).map(c => c.id === id ? { ...c, text } : c) } : d));
  };
  const deleteComment = (id: string) => {
    setDraft(d => (d ? { ...d, comments: (d.comments ?? []).filter(c => c.id !== id) } : d));
  };
  const toggleReaction = (id: string, emoji: string) => {
    setDraft(d => {
      if (!d) return d;
      return { ...d, comments: (d.comments ?? []).map(c => {
        if (c.id !== id) return c;
        const r = { ...(c.reactions ?? {}) };
        if (r[emoji]) { delete r[emoji]; } else { r[emoji] = 1; }
        return { ...c, reactions: r };
      }) };
    });
  };

  const isProduct  = item?.kind === 'product';
  const isEpic     = item?.kind === 'epic';     // "component" in the user's terminology
  const isFeature  = item?.kind === 'feature';
  const isInitiative = item?.kind === 'initiative';
  const isRelease    = item?.kind === 'release';
  const isObjective  = item?.kind === 'objective';
  const isReleaseGroup = item?.kind === 'release-group';
  // Objectives + initiatives share the same popup shape (goal-style): Health tab,
  // Work-progress-and-Health / Features sections, and the reduced field set.
  const isGoal = isObjective || isInitiative;
  // Fields shared by all entity kinds.
  // Status / Timeframe / Value stay feature-only; Hierarchy parent is hidden
  // for top-level entities.
  const isWorkItem = isFeature || isEpic || isProduct || isInitiative || isRelease || isObjective || isReleaseGroup;
  const isTopLevel = isProduct || isInitiative || isRelease || isObjective || isReleaseGroup; // no hierarchy-parent field
  // Human label for the current kind (used in the 3-dot menu / delete confirm).
  const kindNoun = isObjective ? 'objective' : isInitiative ? 'initiative' : isRelease ? 'release'
    : isEpic ? 'component' : isProduct ? 'product' : isReleaseGroup ? 'release group' : 'feature';

  const memberById = (id?: string | null) => id ? members.find(m => m.user_id === id) ?? null : null;

  /* ── "Hierarchy parent" picker ───────────────────────────────
     • For a component, parent must be a product (required).
     • For a feature, parent can be a component (preferred) OR a product
       directly. Vice versa is NOT possible.
     • For a product, the hierarchy parent field is hidden.           */
  const allowedParentKinds = isEpic ? 'product' : isFeature ? 'component-or-product' : null;
  const validParents = useMemo(() => {
    if (!allowedParentKinds) return [];
    return parentCandidates.filter(p => {
      if (allowedParentKinds === 'product') return p.kind === 'product';
      return p.kind === 'product' || p.kind === 'epic';
    });
  }, [allowedParentKinds, parentCandidates]);

  if (!item || !draft) return null;

  const parentName = (id?: string | null) => {
    if (!id) return null;
    return parentCandidates.find(p => p.id === id)?.name ?? null;
  };

  const title = isProduct ? 'Edit product' : isEpic ? 'Edit component' : isInitiative ? 'Edit initiative' : isRelease ? 'Edit release' : isReleaseGroup ? 'Edit release group' : isObjective ? 'Edit objective' : 'Edit feature';
  const isCreateMode = !item.id || (item.name === '' || item.name?.startsWith('New '));

  const onField = <K extends keyof PrioritizeItem>(k: K, v: PrioritizeItem[K]) =>
    setDraft(d => (d ? { ...d, [k]: v } : d));

  // Toggle an id within one of the feature's link arrays (dependency/company/user/document).
  const toggleLinkId = (field: 'dependencyIds' | 'companyIds' | 'userIds' | 'documentIds', id: string) =>
    setDraft(d => {
      if (!d) return d;
      const cur = (d[field] as string[] | undefined) ?? [];
      const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
      return { ...d, [field]: next };
    });

  // Release / release-group derived data (from the optional full item list).
  const releaseGroupCandidates = (allItems ?? parentCandidates).filter(i => i.kind === 'release-group');
  // Features linked to this item — to a release via releaseParentId, to an objective
  // via objectiveParentId. Used by the Features picker and the work-progress rollup.
  const linkField: 'releaseParentId' | 'objectiveParentId' | 'initiativeParentId' =
    isObjective ? 'objectiveParentId' : isInitiative ? 'initiativeParentId' : 'releaseParentId';
  const releaseFeatures = (allItems ?? []).filter(i => i.kind === 'feature' && i[linkField] === item.id);
  // Releases that belong to this release-group (for the group popup's Releases section).
  const groupReleases = (allItems ?? []).filter(i => i.kind === 'release' && i.releaseGroupParentId === item.id);

  // Auto-rollup work progress: average each linked feature's completeness,
  // measured by its position in the status sequence (new-idea → released).
  const FEATURE_COMPLETENESS: Record<string, number> = {
    'new-idea': 0, 'candidate': 25, 'planned': 50, 'in-progress': 75,
    'backlog': 0, 'done': 100, 'released': 100,
  };
  const autoRollupProgress = (): number => {
    if (releaseFeatures.length === 0) return 0;
    const total = releaseFeatures.reduce((sum, f) => sum + (FEATURE_COMPLETENESS[f.status ?? 'new-idea'] ?? 0), 0);
    return Math.round(total / releaseFeatures.length);
  };
  const wpMode = draft.workProgressMode ?? 'auto';
  const wpValue = wpMode === 'auto' ? autoRollupProgress() : (draft.workProgress ?? 0);

  return (
    <>
    <div className="cum-modal-backdrop" onClick={onClose}>
      <div
        className="inline-edit-form premium-task-modal cum-company-modal prioritize-detail-modal"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header (same pattern as InsightModal) ── */}
        <div className="task-modal-header">
          <div className="header-titles-new">
            <h3>{isCreateMode ? title.replace('Edit', 'Create') : title}</h3>
            <p className="header-subtitle-new">
              {isProduct
                ? 'A product is the top-level entity — owns components and features.'
                : isEpic
                  ? 'A component groups related features together under a product.'
                  : isObjective
                    ? 'An objective is a measurable goal that features contribute toward.'
                    : isInitiative
                      ? 'An initiative groups the features that deliver a strategic goal.'
                      : isRelease
                        ? 'A release groups the features shipped together.'
                        : 'A feature is a single unit of work, owned by a component or product.'}
            </p>
          </div>
          <div className="cum-header-actions">
            {(isRelease || isObjective || isInitiative || isFeature || isEpic || isProduct) && (
              <div className="cum-menu-wrapper" ref={menuRef}>
                <button type="button" className="cum-dots-btn" onClick={() => setShowMenu(p => !p)} title="More options">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                  </svg>
                </button>
                {showMenu && (
                  <div className="cum-context-menu">
                    <button className="cum-menu-item" onClick={() => { navigator.clipboard.writeText(window.location.href).catch(() => {}); setShowMenu(false); }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                      Copy link
                    </button>
                    <div className="cum-menu-divider" />
                    <button className="cum-menu-item" onClick={() => setShowMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                      Duplicate
                    </button>
                    <button className="cum-menu-item cum-menu-item-muted" onClick={() => setShowMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                      Archive
                    </button>
                    {onDelete && (
                      <button
                        className="cum-menu-item cum-menu-item-danger"
                        disabled={(isRelease || isObjective) && releaseFeatures.length > 0}
                        title={(isRelease || isObjective) && releaseFeatures.length > 0 ? `Remove the ${releaseFeatures.length} linked feature${releaseFeatures.length > 1 ? 's' : ''} before deleting this ${kindNoun}.` : `Delete this ${kindNoun}`}
                        onClick={() => {
                          if ((isRelease || isObjective) && releaseFeatures.length > 0) return;
                          setShowMenu(false);
                          setConfirmDelete(true);
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                        Delete {kindNoun}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              className="task-modal-close"
              onClick={onClose}
              title="Close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* ── Two-column body (matches InsightModal layout) ── */}
        <div className="task-modal-content-layout">

          {/* ── LEFT — Title + tabbed content ── */}
          <div className="task-modal-main-column">
            <div className="form-group-new">
              <label className="section-label-new">
                Title <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div className="title-input-wrapper-new">
                <input
                  type="text"
                  className="task-title-input-new"
                  placeholder={isProduct ? 'Product name' : isEpic ? 'Component name' : isInitiative ? 'Initiative name' : isRelease ? 'Release name' : isReleaseGroup ? 'Release group name' : isObjective ? 'Objective name' : 'Feature name'}
                  value={draft.name}
                  onChange={e => onField('name', e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            {/* Tabs — features get the full set; components/products get
                Description / Insights / History only. */}
            {isWorkItem && (
              <div className="cum-tabs cum-tabs-inline">
                {(isFeature
                  ? (['description', 'insights', 'health', 'release-notes', 'history'] as TabKey[])
                  : isGoal
                    ? (['description', 'health', 'history'] as TabKey[])
                    : (isRelease || isReleaseGroup)
                      ? (['description', 'history'] as TabKey[])
                      : (['description', 'insights', 'history'] as TabKey[])
                ).map(tab => (
                  <button
                    key={tab}
                    type="button"
                    className={`cum-tab ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === 'description' ? 'Description' :
                     tab === 'insights' ? 'Insights' :
                     tab === 'health' ? 'Health' :
                     tab === 'release-notes' ? 'Release Notes' : 'History'}
                  </button>
                ))}
              </div>
            )}

            {/* Description tab — features + components + products */}
            {isWorkItem && activeTab === 'description' && (
              <>
                <div className="form-group-new">
                  <TaskDescriptionEditor
                    key={`prm-desc-${item.id}`}
                    htmlValue={draft.description || ''}
                    onChange={v => onField('description', v)}
                    placeholder={isProduct
                      ? 'Describe this product — its purpose and scope…'
                      : isEpic
                        ? 'Describe this component — what features does it group?'
                        : isRelease
                          ? 'Describe this release — what is shipping and when…'
                          : isReleaseGroup
                            ? 'Describe this release group…'
                            : isInitiative
                              ? 'Describe this initiative — its strategic goal…'
                              : 'Describe this feature — what is the user-facing behavior?'}
                  />
                </div>

                {/* Attachment previews */}
                {(draft.attachments?.length ?? 0) > 0 && (
                  <div className="cum-attachment-previews">
                    {draft.attachments!.map(att => (
                      <div key={att.id} className="cum-att-thumb">
                        {att.type.startsWith('image/') ? (
                          <img src={att.url} alt={att.name} className="cum-att-img" onClick={() => setLightboxUrl(att.url)} title={att.name} />
                        ) : (
                          <div className="cum-att-file" title={att.name}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            <span className="cum-att-file-name">{att.name.length > 14 ? att.name.slice(0, 12) + '…' : att.name}</span>
                          </div>
                        )}
                        <button type="button" className="cum-att-remove" onClick={() => removeAttachment(att.id)} title="Remove">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Attachment button */}
                <div className="form-group-new">
                  <input ref={fileInputRef} type="file" style={{ display: 'none' }} multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={handleFileChange} />
                  <div className="integration-buttons-grid-new">
                    <div className="integrations-grid-row-new">
                      <button type="button" className="integration-btn-new" onClick={() => fileInputRef.current?.click()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                        Attachment
                      </button>
                    </div>
                  </div>
                </div>

                {/* Linked sections (feature only) — Work progress + searchable
                    link pickers (dependencies / objectives / releases / companies
                    / users / documents). Integrations removed. */}
                {isFeature && (() => {
                  const wp = draft.workProgress ?? 0;
                  // Data sources for the pickers.
                  const allFeatures = (allItems ?? []).filter(i => i.kind === 'feature' && i.id !== item.id);
                  const allObjectives = (allItems ?? []).filter(i => i.kind === 'objective');
                  const allReleases = (allItems ?? []).filter(i => i.kind === 'release');
                  const subFeatures = (allItems ?? []).filter(i => i.kind === 'feature' && i.parentId === item.id);
                  const depIds = draft.dependencyIds ?? [];
                  const compIds = draft.companyIds ?? [];
                  const userIds = draft.userIds ?? [];
                  const docIds = draft.documentIds ?? [];
                  const fq = linkQuery.trim().toLowerCase();

                  // Generic picker dropdown for a section.
                  const renderPicker = (
                    rows: Array<{ id: string; label: string; sub?: string; checked: boolean; onToggle: () => void }>,
                    emptyText: string,
                  ) => (
                    <div className="prm-feat-picker" onClick={e => e.stopPropagation()}>
                      <input className="prm-feat-picker-search" autoFocus placeholder="Search…" value={linkQuery} onChange={e => setLinkQuery(e.target.value)} />
                      <div className="prm-feat-picker-list">
                        {rows.length === 0 ? <div className="prm-feat-picker-empty">{emptyText}</div> :
                          rows.map(r => (
                            <button type="button" key={r.id} className={`prm-feat-picker-item ${r.checked ? 'checked' : ''}`} onClick={r.onToggle}>
                              <span className="prm-feat-picker-check">{r.checked && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}</span>
                              <span className="prm-feat-picker-name">{r.label}{r.sub ? <span className="prm-feat-picker-sub"> · {r.sub}</span> : null}</span>
                            </button>
                          ))}
                      </div>
                    </div>
                  );

                  type SectionDef = {
                    key: string; label: string; count: number;
                    rows: Array<{ id: string; name: string; color?: string }>;
                    picker: React.ReactNode;
                  };
                  const sections: SectionDef[] = [
                    {
                      key: 'dependencies', label: 'Dependencies', count: depIds.length,
                      rows: allFeatures.filter(f => depIds.includes(f.id)).map(f => ({ id: f.id, name: f.name, color: f.color })),
                      picker: renderPicker(
                        allFeatures.filter(f => !fq || f.name.toLowerCase().includes(fq)).map(f => ({ id: f.id, label: f.name, checked: depIds.includes(f.id), onToggle: () => toggleLinkId('dependencyIds', f.id) })),
                        'No other features yet.',
                      ),
                    },
                    {
                      key: 'objectives', label: 'Objectives', count: draft.objectiveParentId ? 1 : 0,
                      rows: allObjectives.filter(o => o.id === draft.objectiveParentId).map(o => ({ id: o.id, name: o.name, color: o.color })),
                      picker: renderPicker(
                        allObjectives.filter(o => !fq || o.name.toLowerCase().includes(fq)).map(o => ({ id: o.id, label: o.name, checked: draft.objectiveParentId === o.id, onToggle: () => onField('objectiveParentId', draft.objectiveParentId === o.id ? null : o.id) })),
                        'No objectives yet.',
                      ),
                    },
                    {
                      key: 'releases', label: 'Releases', count: draft.releaseParentId ? 1 : 0,
                      rows: allReleases.filter(r => r.id === draft.releaseParentId).map(r => ({ id: r.id, name: r.name, color: r.color })),
                      picker: renderPicker(
                        allReleases.filter(r => !fq || r.name.toLowerCase().includes(fq)).map(r => ({ id: r.id, label: r.name, checked: draft.releaseParentId === r.id, onToggle: () => onField('releaseParentId', draft.releaseParentId === r.id ? null : r.id) })),
                        'No releases yet.',
                      ),
                    },
                    {
                      key: 'companies', label: 'Companies', count: compIds.length,
                      rows: (companies ?? []).filter(c => compIds.includes(c.id)).map(c => ({ id: c.id, name: c.name })),
                      picker: renderPicker(
                        (companies ?? []).filter(c => !fq || c.name.toLowerCase().includes(fq)).map(c => ({ id: c.id, label: c.name, checked: compIds.includes(c.id), onToggle: () => toggleLinkId('companyIds', c.id) })),
                        'No companies — add them in the Customers module.',
                      ),
                    },
                    {
                      key: 'users', label: 'Users', count: userIds.length,
                      rows: (customerUsers ?? []).filter(u => userIds.includes(u.id)).map(u => ({ id: u.id, name: u.name })),
                      picker: renderPicker(
                        (customerUsers ?? []).filter(u => !fq || u.name.toLowerCase().includes(fq) || (u.email ?? '').toLowerCase().includes(fq)).map(u => ({ id: u.id, label: u.name, sub: u.email ?? undefined, checked: userIds.includes(u.id), onToggle: () => toggleLinkId('userIds', u.id) })),
                        'No users — add them in the Customers module.',
                      ),
                    },
                    {
                      key: 'documents', label: 'Documents', count: docIds.length,
                      rows: (documents ?? []).filter(d => docIds.includes(d.id)).map(d => ({ id: d.id, name: d.title })),
                      picker: renderPicker(
                        (documents ?? []).filter(d => !fq || d.title.toLowerCase().includes(fq)).map(d => ({ id: d.id, label: d.title, checked: docIds.includes(d.id), onToggle: () => toggleLinkId('documentIds', d.id) })),
                        'No documents — create them in Docs Writer.',
                      ),
                    },
                  ];

                  return (
                  <div className="cum-sections-list" style={{ marginTop: 4 }}>
                    {/* Work progress (above Dependencies) */}
                    {(() => {
                      const open = openSections.has('feat-work-progress');
                      return (
                        <div className="cum-section">
                          <button type="button" className="cum-section-header" onClick={() => toggleSection('feat-work-progress')}>
                            <svg className={`cum-section-chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            <span className="cum-section-label">Work progress</span>
                          </button>
                          {open && (
                            <div className="prm-wp-row">
                              <span className="prm-wp-icon">%</span>
                              <span className="prm-wp-label">Work progress</span>
                              <input type="range" min={0} max={100} value={wp} className="prm-wp-range" onClick={e => e.stopPropagation()} onChange={e => onField('workProgress', Math.min(100, Math.max(0, Number(e.target.value))))} />
                              <input type="number" min={0} max={100} value={wp} className="prm-wp-num-input" onClick={e => e.stopPropagation()} onChange={e => onField('workProgress', e.target.value === '' ? 0 : Math.min(100, Math.max(0, Number(e.target.value))))} />
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Dependencies (first link section) */}
                    {(() => {
                      const s = sections[0]; const open = openSections.has(s.key);
                      return (
                        <div className="cum-section">
                          <div className="cum-section-header-row">
                            <button type="button" className="cum-section-header" onClick={() => toggleSection(s.key)}>
                              <svg className={`cum-section-chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                              <span className="cum-section-label">{s.label}</span>
                              <span className="cum-section-count">{s.count}</span>
                            </button>
                            <div className="prm-feat-picker-wrap" ref={linkPicker === s.key ? linkPickerRef : undefined}>
                              <button type="button" className="cum-section-add" title={`Add ${s.label.toLowerCase()}`} onClick={e => { e.stopPropagation(); setLinkQuery(''); setLinkPicker(linkPicker === s.key ? null : s.key); }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
                              {linkPicker === s.key && s.picker}
                            </div>
                          </div>
                          {open && (s.rows.length ? (
                            <div className="cum-section-body">
                              {s.rows.map(r => (
                                <div key={r.id} className="cum-section-row">
                                  <span className="cum-section-row-icon"><span className="prioritize-symbol-feature" style={{ width: 12, height: 12, background: r.color || 'var(--brand-500)' }} /></span>
                                  <span className="cum-section-row-name">{r.name}</span>
                                  <button type="button" className="cum-section-row-remove" title="Remove" onClick={() => toggleLinkId('dependencyIds', r.id)}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                                </div>
                              ))}
                            </div>
                          ) : <div className="cum-section-empty">No dependencies linked yet</div>)}
                        </div>
                      );
                    })()}

                    {/* Subfeatures (read-only, like the release popup) */}
                    {(() => {
                      const open = openSections.has('feat-subfeatures');
                      return (
                        <div className="cum-section">
                          <button type="button" className="cum-section-header" onClick={() => toggleSection('feat-subfeatures')}>
                            <svg className={`cum-section-chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            <span className="cum-section-label">Subfeatures</span>
                            <span className="cum-section-count">{subFeatures.length}</span>
                          </button>
                          {open && (subFeatures.length ? (
                            <div className="cum-section-body">
                              {subFeatures.map(f => (
                                <div key={f.id} className="cum-section-row">
                                  <span className="cum-section-row-icon"><span className="prioritize-symbol-feature" style={{ width: 12, height: 12, background: f.color || 'var(--brand-500)' }} /></span>
                                  <span className="cum-section-row-name">{f.name}</span>
                                </div>
                              ))}
                            </div>
                          ) : <div className="cum-section-empty">No subfeatures yet</div>)}
                        </div>
                      );
                    })()}

                    {/* Remaining link sections (objectives/releases/companies/users/documents) */}
                    {sections.slice(1).map(s => {
                      const open = openSections.has(s.key);
                      const removeFor = s.key === 'objectives' ? () => onField('objectiveParentId', null)
                        : s.key === 'releases' ? () => onField('releaseParentId', null)
                        : s.key === 'companies' ? (id: string) => toggleLinkId('companyIds', id)
                        : s.key === 'users' ? (id: string) => toggleLinkId('userIds', id)
                        : (id: string) => toggleLinkId('documentIds', id);
                      return (
                        <div key={s.key} className="cum-section">
                          <div className="cum-section-header-row">
                            <button type="button" className="cum-section-header" onClick={() => toggleSection(s.key)}>
                              <svg className={`cum-section-chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                              <span className="cum-section-label">{s.label}</span>
                              <span className="cum-section-count">{s.count}</span>
                            </button>
                            <div className="prm-feat-picker-wrap" ref={linkPicker === s.key ? linkPickerRef : undefined}>
                              <button type="button" className="cum-section-add" title={`Add ${s.label.toLowerCase()}`} onClick={e => { e.stopPropagation(); setLinkQuery(''); setLinkPicker(linkPicker === s.key ? null : s.key); }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
                              {linkPicker === s.key && s.picker}
                            </div>
                          </div>
                          {open && (s.rows.length ? (
                            <div className="cum-section-body">
                              {s.rows.map(r => (
                                <div key={r.id} className="cum-section-row">
                                  <span className="cum-section-row-icon"><span className="prioritize-symbol-feature" style={{ width: 12, height: 12, background: (r as any).color || '#94a3b8' }} /></span>
                                  <span className="cum-section-row-name">{r.name}</span>
                                  <button type="button" className="cum-section-row-remove" title="Remove" onClick={() => removeFor(r.id)}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                                </div>
                              ))}
                            </div>
                          ) : <div className="cum-section-empty">No {s.label.toLowerCase()} linked yet</div>)}
                        </div>
                      );
                    })}
                  </div>
                  );
                })()}

                {/* Linked sections (component only) — collapsible, like the
                    edit-company popup. Placeholders for now. */}
                {isEpic && (
                  <div className="cum-sections-list" style={{ marginTop: 4 }}>
                    {([
                      'Child Components', 'Features', 'Companies', 'Users', 'Integrations',
                    ]).map(label => {
                      const key = 'comp-' + label.toLowerCase().replace(/\s+/g, '-');
                      const open = openSections.has(key);
                      return (
                        <div key={key} className="cum-section">
                          <button type="button" className="cum-section-header" onClick={() => toggleSection(key)}>
                            <svg className={`cum-section-chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6"/>
                            </svg>
                            <span className="cum-section-label">{label}</span>
                            <span className="cum-section-count">0</span>
                            <button type="button" className="cum-section-add" onClick={e => e.stopPropagation()} title={`Add ${label.toLowerCase()}`}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                              </svg>
                            </button>
                          </button>
                          {open && (
                            <div className="cum-section-empty">No {label.toLowerCase()} linked yet</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Linked sections (product only) — collapsible, like the
                    edit-company popup. Placeholders for now. */}
                {isProduct && (
                  <div className="cum-sections-list" style={{ marginTop: 4 }}>
                    {([
                      'Components', 'Features', 'Companies', 'Users', 'Integrations',
                    ]).map(label => {
                      const key = 'prod-' + label.toLowerCase().replace(/\s+/g, '-');
                      const open = openSections.has(key);
                      return (
                        <div key={key} className="cum-section">
                          <button type="button" className="cum-section-header" onClick={() => toggleSection(key)}>
                            <svg className={`cum-section-chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6"/>
                            </svg>
                            <span className="cum-section-label">{label}</span>
                            <span className="cum-section-count">0</span>
                            <button type="button" className="cum-section-add" onClick={e => e.stopPropagation()} title={`Add ${label.toLowerCase()}`}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                              </svg>
                            </button>
                          </button>
                          {open && (
                            <div className="cum-section-empty">No {label.toLowerCase()} linked yet</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Linked sections (release + objective + initiative) — Work progress + Features. */}
                {(isRelease || isGoal) && (
                  <div className="cum-sections-list" style={{ marginTop: 4 }}>
                    {/* Work progress — progress bar + % + gear menu (auto/manual) */}
                    {(() => {
                      const open = openSections.has('rel-work-progress');
                      return (
                        <div className="cum-section">
                          <button type="button" className="cum-section-header" onClick={() => toggleSection('rel-work-progress')}>
                            <svg className={`cum-section-chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            <span className="cum-section-label">Work progress</span>
                          </button>
                          {open && (
                            <>
                            <div className="prm-wp-row">
                              <span className="prm-wp-icon">%</span>
                              <span className="prm-wp-label">Work progress</span>
                              {wpMode === 'manual' ? (
                                <input
                                  type="range" min={0} max={100} value={wpValue}
                                  className="prm-wp-range"
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => onField('workProgress', Math.min(100, Math.max(0, Number(e.target.value))))}
                                />
                              ) : (
                                <span className="prm-wp-track"><span className="prm-wp-fill" style={{ width: `${wpValue}%` }} /></span>
                              )}
                              {wpMode === 'manual' ? (
                                <input
                                  type="number" min={0} max={100} value={wpValue}
                                  className="prm-wp-num-input"
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => {
                                    const v = e.target.value === '' ? 0 : Math.min(100, Math.max(0, Number(e.target.value)));
                                    onField('workProgress', v);
                                  }}
                                />
                              ) : (
                                <span className="prm-wp-num">{wpValue}%</span>
                              )}
                              <div className="prm-wp-gear-wrap" ref={wpMenuRef}>
                                <button type="button" className="prm-wp-gear" title="Work progress source" onClick={e => { e.stopPropagation(); setWpMenuOpen(o => !o); }}>
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                                </button>
                                {wpMenuOpen && (
                                  <div className="prm-wp-menu" onClick={e => e.stopPropagation()}>
                                    <button type="button" className={`prm-wp-menu-item ${wpMode === 'manual' ? 'active' : ''}`} onClick={() => { onField('workProgressMode', 'manual'); setWpMenuOpen(false); }}>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                      Update manually
                                    </button>
                                    <button type="button" className={`prm-wp-menu-item ${wpMode === 'auto' ? 'active' : ''}`} onClick={() => { onField('workProgressMode', 'auto'); onField('workProgress', autoRollupProgress()); setWpMenuOpen(false); }}>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                                      Auto-roll up from linked items
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {/* Features — live count + list of linked features */}
                    {(() => {
                      const open = openSections.has('rel-features');
                      return (
                        <div className="cum-section">
                          <div className="cum-section-header-row">
                            <button type="button" className="cum-section-header" onClick={() => toggleSection('rel-features')}>
                              <svg className={`cum-section-chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                              <span className="cum-section-label">Features</span>
                              <span className="cum-section-count">{releaseFeatures.length}</span>
                            </button>
                            <div className="prm-feat-picker-wrap" ref={featPickerRef}>
                              <button type="button" className="cum-section-add" onClick={e => { e.stopPropagation(); setFeatPickerQuery(''); setFeatPickerOpen(o => !o); }} title="Add feature"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
                              {featPickerOpen && (
                                <div className="prm-feat-picker" onClick={e => e.stopPropagation()}>
                                  <input
                                    className="prm-feat-picker-search"
                                    autoFocus
                                    placeholder="Search features…"
                                    value={featPickerQuery}
                                    onChange={e => setFeatPickerQuery(e.target.value)}
                                  />
                                  <div className="prm-feat-picker-list">
                                    {(() => {
                                      const all = (allItems ?? []).filter(i => i.kind === 'feature');
                                      const fq = featPickerQuery.trim().toLowerCase();
                                      const shown = fq ? all.filter(f => f.name.toLowerCase().includes(fq)) : all;
                                      if (all.length === 0) return <div className="prm-feat-picker-empty">No features yet — create features on the Features page first.</div>;
                                      if (shown.length === 0) return <div className="prm-feat-picker-empty">No matches</div>;
                                      return shown.map(f => {
                                        const linked = f[linkField] === item.id;
                                        const linkedElsewhere = !!f[linkField] && f[linkField] !== item.id;
                                        return (
                                          <button
                                            type="button"
                                            key={f.id}
                                            className={`prm-feat-picker-item ${linked ? 'checked' : ''}`}
                                            disabled={linkedElsewhere}
                                            title={linkedElsewhere ? `Already linked to another ${isObjective ? 'objective' : isInitiative ? 'initiative' : 'release'}` : ''}
                                            onClick={() => onToggleFeature?.(f.id, !linked)}
                                          >
                                            <span className="prm-feat-picker-check">
                                              {linked && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                                            </span>
                                            <span className="prioritize-symbol-feature" style={{ width: 11, height: 11, background: f.color || 'var(--brand-500)' }} />
                                            <span className="prm-feat-picker-name">{f.name}</span>
                                            {linkedElsewhere && <span className="prm-feat-picker-tag">linked</span>}
                                          </button>
                                        );
                                      });
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          {open && (
                            releaseFeatures.length > 0 ? (
                              <div className="cum-section-body">
                                {releaseFeatures.map(f => (
                                  <div key={f.id} className="cum-section-row">
                                    <span className="cum-section-row-icon"><span className="prioritize-symbol-feature" style={{ width: 12, height: 12, background: f.color || 'var(--brand-500)' }} /></span>
                                    <span className="cum-section-row-name">{f.name}</span>
                                    <button type="button" className="cum-section-row-remove" title={`Remove from ${isObjective ? 'objective' : isInitiative ? 'initiative' : 'release'}`} onClick={() => onToggleFeature?.(f.id, false)}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                                  </div>
                                ))}
                              </div>
                            ) : <div className="cum-section-empty">No features linked yet</div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Subfeatures — features nested under this release (release only) */}
                    {isRelease && (() => {
                      const subOpen = openSections.has('rel-subfeatures');
                      const subFeatures = (allItems ?? []).filter(i => i.kind === 'feature' && i.parentId === item.id);
                      return (
                        <div className="cum-section">
                          <button type="button" className="cum-section-header" onClick={() => toggleSection('rel-subfeatures')}>
                            <svg className={`cum-section-chevron ${subOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            <span className="cum-section-label">Subfeatures</span>
                            <span className="cum-section-count">{subFeatures.length}</span>
                          </button>
                          {subOpen && (
                            subFeatures.length > 0 ? (
                              <div className="cum-section-body">
                                {subFeatures.map(f => (
                                  <div key={f.id} className="cum-section-row">
                                    <span className="cum-section-row-icon"><span className="prioritize-symbol-feature" style={{ width: 12, height: 12, background: f.color || 'var(--brand-500)' }} /></span>
                                    <span className="cum-section-row-name">{f.name}</span>
                                  </div>
                                ))}
                              </div>
                            ) : <div className="cum-section-empty">No subfeatures yet</div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Linked sections (release-group only) — Releases (live, inline-create). */}
                {isReleaseGroup && (
                  <div className="cum-sections-list" style={{ marginTop: 4 }}>
                    {(() => {
                      const open = openSections.has('rg-releases');
                      const createRel = () => {
                        const name = relInlineName.trim();
                        if (name && onCreateRelease) onCreateRelease(name, item.id);
                        setRelInlineName('');
                        setRelInlineOpen(false);
                      };
                      return (
                        <div className="cum-section">
                          <button type="button" className="cum-section-header" onClick={() => toggleSection('rg-releases')}>
                            <svg className={`cum-section-chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            <span className="cum-section-label">Releases</span>
                            <span className="cum-section-count">{groupReleases.length}</span>
                            <button
                              type="button"
                              className="cum-section-add"
                              title="Add release"
                              onClick={e => { e.stopPropagation(); toggleSection('rg-releases'); setOpenSections(prev => new Set(prev).add('rg-releases')); setRelInlineOpen(true); setRelInlineName(''); }}
                            ><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
                          </button>
                          {open && (
                            <div className="cum-section-body prm-rg-section-body">
                              {groupReleases.map(r => (
                                <div key={r.id} className="cum-section-row prm-rg-release-row">
                                  <span className="cum-section-row-icon"><svg width="15" height="15" viewBox="0 0 24 24" fill={r.color || '#3b82f6'} stroke="none"><path d="M5 3v18l7-5 7 5V3z"/></svg></span>
                                  <span className="cum-section-row-name">{r.name}</span>
                                </div>
                              ))}
                              {relInlineOpen ? (
                                <div className="cum-section-row prm-rg-release-row prm-rg-inline-row">
                                  <span className="cum-section-row-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="#9ca3af" stroke="none"><path d="M5 3v18l7-5 7 5V3z"/></svg></span>
                                  <input
                                    className="prm-rg-inline-input"
                                    autoFocus
                                    placeholder="Enter release name…"
                                    value={relInlineName}
                                    onChange={e => setRelInlineName(e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') { e.preventDefault(); createRel(); }
                                      if (e.key === 'Escape') { setRelInlineName(''); setRelInlineOpen(false); }
                                    }}
                                    onBlur={() => { if (relInlineName.trim()) createRel(); else setRelInlineOpen(false); }}
                                  />
                                  <span className="prm-rg-inline-enter">↵</span>
                                </div>
                              ) : (
                                groupReleases.length === 0 && <div className="cum-section-empty">No releases added to this release group.</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Comments — last section, below the linked sections */}
                <div className="form-group-new comments-group-new">
                  <div className="prm-comments-title">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    <span>Comments</span>
                    {(draft.comments?.length ?? 0) > 0 && <span className="comment-tab-badge">{draft.comments!.length}</span>}
                  </div>

                  {/* Input above the records */}
                  <div className="comment-input-row-new">
                    <div className="comment-avatar-new">{(user?.user_metadata?.full_name || user?.email || 'U')?.charAt(0)?.toUpperCase()}</div>
                    <div className="comment-input-box-wrapper-new">
                      <textarea
                        className="comment-textarea-new"
                        placeholder="Add a comment…"
                        value={commentInput}
                        onChange={e => setCommentInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                        rows={1}
                      />
                      <div className="comment-input-actions-new">
                        <button type="button" className="comment-send-btn-new" onClick={addComment} disabled={!commentInput.trim()} title="Send Comment">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Records below the input (newest first) */}
                  {(draft.comments?.length ?? 0) > 0 ? (
                    <div className="comments-feed-new">
                      {[...draft.comments!].reverse().map(c => (
                        <CommentCard
                          key={c.id}
                          comment={c}
                          onEdit={editComment}
                          onDelete={deleteComment}
                          onReact={toggleReaction}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="comment-empty-state">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      <span>No comments yet</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Insights tab — features + components + products */}
            {isWorkItem && activeTab === 'insights' && (
              <div className="form-group-new">
                <div className="prm-health-header">
                  <span>Insights</span>
                  <button type="button" className="prm-new-health-btn" onClick={() => { setEditingInsight(null); setAddInsightOpen(true); }}>Add insight</button>
                </div>
                <div className="prm-insights-list">
                  {draft.insights?.length ? (
                    draft.insights.map(insight => (
                      <div key={insight.id} className="prm-insight-card">
                        <div className="prm-insight-header">
                          <div className="prm-insight-meta">
                            <div className="prm-company-logo" />
                            <span className="prm-company-name">{insight.companyName}</span>
                            {insight.importance != null && (
                              <span className="prm-insight-dots" title="Importance for this feature">
                                {[1, 2, 3, 4].map(i => (
                                  <span key={i} className={`prm-insight-dot ${i <= (insight.importance ?? 0) ? 'on' : ''}`} />
                                ))}
                              </span>
                            )}
                          </div>
                          <div className="prm-card-actions">
                            <button type="button" className="prm-comment-action-link" onClick={() => { setEditingInsight(insight); setAddInsightOpen(true); }}>Edit</button>
                            <button type="button" className="prm-comment-action-link" onClick={() => deleteInsight(insight.id)}>Delete</button>
                          </div>
                        </div>
                        {insight.title && <div className="prm-insight-title">{insight.title}</div>}
                        <div className="prm-insight-note">{insight.note}</div>
                        <div className="prm-insight-footer">
                          <span className="prm-insight-date">{insight.createdAt}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="prm-insights-empty">No insights linked yet. Click “Add insight” to create one.</div>
                  )}
                </div>
              </div>
            )}

            {/* Health tab (features only) */}
            {(isFeature || isGoal) && activeTab === 'health' && (
              <div className="form-group-new">
                <div className="prm-health-header">
                  <span>Health updates</span>
                  <button type="button" className="prm-new-health-btn" onClick={() => openHealthUpdateModal()}>New update</button>
                </div>
                <div className="prm-health-list">
                  {draft.healthUpdates?.length ? (
                    draft.healthUpdates.slice().reverse().map((update, idx) => (
                      <div key={idx} className="prm-entry-card">
                        <div className="prm-entry-head">
                          <span className={`prm-health-status-tag prm-health-tag-${update.status}`}>
                            <span className="prm-status-dot" />
                            <span className="prm-status-text">{update.label}</span>
                          </span>
                          <div className="prm-card-actions">
                            <span className="prm-author">{update.by}</span>
                            <span className="prm-timestamp">{update.at}</span>
                            <button type="button" className="prm-comment-action-link" onClick={() => openHealthUpdateModal(update)}>Edit</button>
                            <button type="button" className="prm-comment-action-link" onClick={() => deleteHealthEntry(update)}>Delete</button>
                          </div>
                        </div>
                        {update.note && <div className="prm-entry-note">{update.note}</div>}
                      </div>
                    ))
                  ) : (
                    <div className="prm-health-empty">No health updates yet. Be the first to post one.</div>
                  )}
                </div>
              </div>
            )}

            {/* Release Notes tab (features only) — list + sub-modal, like Health */}
            {isFeature && activeTab === 'release-notes' && (
              <div className="form-group-new">
                <div className="prm-health-header">
                  <span>Release notes</span>
                  <button type="button" className="prm-new-health-btn" onClick={() => setReleaseNoteOpen(true)}>New release note</button>
                </div>
                <div className="prm-health-list">
                  {draft.releaseNoteEntries?.length ? (
                    draft.releaseNoteEntries.map(entry => (
                      <div key={entry.id} className="prm-entry-card">
                        <div className="prm-entry-head">
                          <span className={`prm-health-status-tag prm-rn-tag-${entry.status}`}>
                            <span className="prm-status-dot" />
                            <span className="prm-status-text">{entry.label}</span>
                          </span>
                          <div className="prm-card-actions">
                            <span className="prm-timestamp">{entry.at}</span>
                            <button type="button" className="prm-comment-action-link" onClick={() => { setEditingReleaseNote(entry); setReleaseNoteOpen(true); }}>Edit</button>
                            <button type="button" className="prm-comment-action-link" onClick={() => deleteReleaseNote(entry.id)}>Delete</button>
                          </div>
                        </div>
                        {entry.note && <div className="prm-entry-note">{entry.note}</div>}
                      </div>
                    ))
                  ) : (
                    <div className="prm-health-empty">No release notes yet. Add the first one.</div>
                  )}
                </div>
              </div>
            )}

            {/* History tab — features + components + products */}
            {isWorkItem && activeTab === 'history' && (
              <div className="cum-insights-placeholder">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <p>Activity history will appear here.</p>
              </div>
            )}
          </div>

          {/* ── RIGHT — All fields (sidebar pattern from InsightModal) ── */}
          <div className="task-modal-sidebar-column prm-sidebar">

            {/* ── Release sidebar (curated field set per the design) ── */}
            {isRelease && (
              <>
                <FieldRow icon="hierarchy" label="Parent release group">
                  <ParentPicker
                    candidates={releaseGroupCandidates}
                    selectedId={draft.releaseGroupParentId}
                    required
                    onChange={id => onField('releaseGroupParentId', id)}
                  />
                </FieldRow>
                <FieldRow icon="bookmark" label="Status">
                  <ReleaseStatusSelect status={draft.status} onChange={s => onField('status', s)} />
                </FieldRow>
                <FieldRow icon="briefcase" label="Owner">
                  <OwnerPicker members={members} selectedId={draft.ownerId} onChange={id => onField('ownerId', id)} />
                </FieldRow>
                <FieldRow icon="calendar" label="Timeframe">
                  <TimeframeField
                    start={draft.startDate}
                    end={draft.endDate}
                    onChange={(s, e) => { onField('startDate', s || undefined); onField('endDate', e || undefined); }}
                  />
                </FieldRow>
                <FieldRow icon="clock" label="Effort">
                  <input type="number" min={0} className="prm-number-input prm-effort-input"
                    value={draft.effort ?? ''} onChange={e => onField('effort', e.target.value === '' ? null : Number(e.target.value))} placeholder="0" />
                </FieldRow>
                <FieldRow icon="checkbox" label="Business Value (level)">
                  <BusinessValueSelect value={draft.businessValueLevel ?? null} onChange={v => onField('businessValueLevel', v)} />
                </FieldRow>
                <FieldRow icon="hash" label="Business Value (score)">
                  <input type="number" min={0} className="prm-number-input prm-effort-input"
                    value={draft.businessValueScore ?? ''} onChange={e => onField('businessValueScore', e.target.value === '' ? null : Number(e.target.value))} placeholder="0" />
                </FieldRow>
                <FieldRow icon="checkbox" label="Overall Stack-rank">
                  <StackRankSelect rank={draft.stackRank ?? null} onChange={n => onField('stackRank', n)} />
                </FieldRow>
              </>
            )}

            {/* ── Release-group sidebar (curated set per image 1) ── */}
            {isReleaseGroup && (
              <>
                <FieldRow icon="briefcase" label="Owner">
                  <OwnerPicker members={members} selectedId={draft.ownerId} onChange={id => onField('ownerId', id)} />
                </FieldRow>
                <FieldRow icon="checkbox" label="Business Value (level)">
                  <BusinessValueSelect value={draft.businessValueLevel ?? null} onChange={v => onField('businessValueLevel', v)} />
                </FieldRow>
                <FieldRow icon="hash" label="Business Value (score)">
                  <input type="number" min={0} className="prm-number-input prm-effort-input"
                    value={draft.businessValueScore ?? ''} onChange={e => onField('businessValueScore', e.target.value === '' ? null : Number(e.target.value))} placeholder="0" />
                </FieldRow>
                <FieldRow icon="checkbox" label="Overall Stack-rank">
                  <StackRankSelect rank={draft.stackRank ?? null} onChange={n => onField('stackRank', n)} />
                </FieldRow>
              </>
            )}

            {/* Generic sidebar (products / components / features / initiatives).
                Releases + release-groups use their own curated sets above. */}
            {!isRelease && !isReleaseGroup && (<>
            {/* For a feature: the FULL 14-field set per the reference image.
                For a component: only the Hierarchy parent field is active
                (per the user's second message). The other fields render
                as "Not assigned" placeholders so the visual layout matches.
                For a product: only the basic fields (no hierarchy parent). */}

            {/* ── Hierarchy parent (component + feature only) ── */}
            {!isTopLevel && (
              <FieldRow icon="hierarchy" label="Hierarchy parent">
                {isEpic ? (
                  // Component → product (REQUIRED)
                  <ParentPicker
                    candidates={validParents}
                    selectedId={draft.parentId}
                    required
                    onChange={id => onField('parentId', id)}
                  />
                ) : (
                  // Feature → component OR product (optional)
                  <ParentPicker
                    candidates={validParents}
                    selectedId={draft.parentId || draft.productParentId}
                    onChange={id => {
                      // Set the right slot based on which kind the picked parent is
                      const picked = validParents.find(p => p.id === id);
                      if (!picked) {
                        onField('parentId', null);
                        onField('productParentId', null);
                        return;
                      }
                      if (picked.kind === 'product') {
                        onField('productParentId', id);
                        onField('parentId', null);
                      } else {
                        onField('parentId', id);
                        onField('productParentId', null);
                      }
                    }}
                  />
                )}
              </FieldRow>
            )}

            {/* ── Status (feature + objective + initiative) ── */}
            {(isFeature || isGoal) && (
              <FieldRow icon="bookmark" label="Status">
                {isGoal
                  ? <ReleaseStatusSelect status={draft.status} onChange={s => onField('status', s)} />
                  : <StatusSelect status={draft.status} onChange={s => onField('status', s)} />}
              </FieldRow>
            )}

            {/* ── Assignee / Owner ── */}
            <FieldRow icon="briefcase" label={isGoal ? 'Owner' : 'Assignee'}>
              <OwnerPicker members={members} selectedId={draft.ownerId} onChange={id => onField('ownerId', id)} />
            </FieldRow>

            {/* ── Timeframe (feature + objective + initiative) ── */}
            {(isFeature || isGoal) && (
              <FieldRow icon="calendar" label="Timeframe">
                <TimeframeField
                  start={draft.startDate}
                  end={draft.endDate}
                  onChange={(s, e) => { onField('startDate', s || undefined); onField('endDate', e || undefined); }}
                />
              </FieldRow>
            )}

            {/* ── Teams (feature + component + objective; not initiative) ── */}
            {isWorkItem && !isInitiative && (
              <FieldRow icon="people" label={isGoal ? 'Team' : 'Teams'}>
                <TeamsPicker value={draft.teams} onChange={v => onField('teams', v)} />
              </FieldRow>
            )}

            {/* ── Tags (was Sample Tag) (feature + component) ── */}
            {isWorkItem && !isGoal && (
              <FieldRow icon="tag" label="Tags">
                <TagsInput value={draft.sampleTag} onChange={v => onField('sampleTag', v)} />
              </FieldRow>
            )}

            {/* ── Effort (feature + component + objective; not initiative) ── */}
            {isWorkItem && !isInitiative && (
              <FieldRow icon="clock" label="Effort">
                <input
                  type="number"
                  min={0}
                  className="prm-number-input prm-effort-input"
                  value={draft.effort ?? ''}
                  onChange={e => onField('effort', e.target.value === '' ? null : Number(e.target.value))}
                  placeholder="0"
                />
              </FieldRow>
            )}

            {/* ── Value (feature + objective + initiative) ── */}
            {(isFeature || isGoal) && (
              <FieldRow icon="gauge" label="Value">
                <ValueGaugeControl
                  value={draft.value ?? null}
                  onChange={v => onField('value', v)}
                />
              </FieldRow>
            )}

            {/* ── Business Value (level) (feature + component) ── */}
            {isWorkItem && (
              <FieldRow icon="checkbox" label={`Business Value ${isEpic ? '(component)' : ''}`.trim()}>
                <BusinessValueSelect
                  value={draft.businessValueLevel ?? null}
                  onChange={v => onField('businessValueLevel', v)}
                />
              </FieldRow>
            )}

            {/* ── Business Value (numeric) (feature + component) ── */}
            {isWorkItem && (
              <FieldRow icon="hash" label="Business Value (score)">
                <input
                  type="number"
                  min={0}
                  className="prm-number-input prm-effort-input"
                  value={draft.businessValueScore ?? ''}
                  onChange={e => onField('businessValueScore', e.target.value === '' ? null : Number(e.target.value))}
                  placeholder="0"
                />
              </FieldRow>
            )}

            {/* ── Overall Stack-rank (feature + component) ── */}
            {isWorkItem && (
              <FieldRow icon="checkbox" label="Overall Stack-rank">
                <StackRankSelect
                  rank={draft.stackRank ?? null}
                  onChange={n => onField('stackRank', n)}
                />
              </FieldRow>
            )}

            {/* ── T-Shirt Sizing (feature + component) ── */}
            {isWorkItem && !isGoal && (
              <FieldRow icon="checkbox" label="T-Shirt Sizing">
                <TShirtSelect
                  size={draft.tshirtSize ?? null}
                  onChange={s => onField('tshirtSize', s)}
                />
              </FieldRow>
            )}

            {/* ── Design Assets (feature + component) ── */}
            {isWorkItem && !isGoal && (
              <FieldRow icon="check-circle" label="Design Assets">
                <IconStatusSelect
                  value={draft.designAssets ?? null}
                  options={STATUS5_OPTIONS}
                  onChange={v => onField('designAssets', v)}
                />
              </FieldRow>
            )}

            {/* ── Designer (feature + component) — same as Assignee ── */}
            {isWorkItem && !isGoal && (
              <FieldRow icon="person" label="Designer">
                <OwnerPicker members={members} selectedId={draft.designerId} onChange={id => onField('designerId', id)} />
              </FieldRow>
            )}

            {/* ── Development (feature + component) ── */}
            {isWorkItem && !isGoal && (
              <FieldRow icon="check-circle" label="Development">
                <IconStatusSelect
                  value={draft.development ?? null}
                  options={STATUS5_OPTIONS}
                  onChange={v => onField('development', v)}
                />
              </FieldRow>
            )}

            {/* ── Launch Ready (feature + component) ── */}
            {isWorkItem && !isGoal && (
              <FieldRow icon="check-circle" label="Launch Ready">
                <IconStatusSelect
                  value={draft.launchReady ?? null}
                  options={LAUNCH_OPTIONS}
                  onChange={v => onField('launchReady', v)}
                />
              </FieldRow>
            )}

            {/* ── Product Spec (feature + component) ── */}
            {isWorkItem && !isGoal && (
              <FieldRow icon="check-circle" label="Product Spec">
                <IconStatusSelect
                  value={draft.productSpec ?? null}
                  options={STATUS5_OPTIONS}
                  onChange={v => onField('productSpec', v)}
                />
              </FieldRow>
            )}

            {/* ── Tech Lead (feature + component) — same as Assignee ── */}
            {isWorkItem && !isGoal && (
              <FieldRow icon="person" label="Tech Lead">
                <OwnerPicker members={members} selectedId={draft.techLeadId} onChange={id => onField('techLeadId', id)} />
              </FieldRow>
            )}
            </>)}
          </div>
        </div>

        {/* ── Footer (matches InsightModal: shell-level, full width) ── */}
        <div className="task-modal-footer">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="submit-btn"
            onClick={() => {
              if (!draft.name.trim()) return;
              if (isRelease && !draft.releaseGroupParentId) { alert('A parent release group is required.'); return; }
              onSave(draft);
            }}
            disabled={!draft.name.trim() || (isRelease && !draft.releaseGroupParentId)}
          >
            {isCreateMode ? title.replace('Edit', 'Create') : 'Save changes'}
          </button>
        </div>
      </div>
    </div>

    {/* Lightbox for image attachments */}
    {lightboxUrl && (
      <div className="cum-lightbox" onClick={() => setLightboxUrl(null)}>
        <img src={lightboxUrl} alt="attachment" className="cum-lightbox-img" onClick={e => e.stopPropagation()} />
        <button className="cum-lightbox-close" onClick={() => setLightboxUrl(null)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    )}

    {healthUpdateModalOpen && (
      <HealthUpdateModal
        item={draft}
        editing={editingHealthEntry}
        onClose={() => { setHealthUpdateModalOpen(false); setEditingHealthEntry(null); }}
        onSave={(entry) => {
          setDraft(d => {
            if (!d) return d;
            const existing = d.healthUpdates || [];
            if (editingHealthEntry) {
              // Replace the entry being edited
              const idx = existing.indexOf(editingHealthEntry);
              const next = [...existing];
              if (idx >= 0) next[idx] = entry;
              return { ...d, healthUpdates: next };
            }
            return { ...d, healthUpdates: [...existing, entry] };
          });
          setHealthUpdateModalOpen(false);
          setEditingHealthEntry(null);
        }}
      />
    )}

    {addInsightOpen && (
      <AddInsightModal
        members={members}
        editing={editingInsight}
        onClose={() => { setAddInsightOpen(false); setEditingInsight(null); }}
        onSave={(entry) => {
          // Persist to the Insights backend so feature insights show on the
          // Insights page. Importance + the linked feature are stored in `links`
          // (NOT tags). Failures fall back to local-only storage.
          const links = [
            { type: 'feature', label: item?.name ?? 'Feature' },
            ...(entry.importance ? [{ type: 'importance', label: String(entry.importance) }] : []),
          ];
          const persist = async (): Promise<string | null> => {
            try {
              if (entry.insightId) {
                await insightService.updateInsight(entry.insightId, {
                  title: entry.title || entry.note.slice(0, 60) || 'Untitled insight',
                  details: entry.note || null,
                  links,
                });
                return entry.insightId;
              }
              const created = await insightService.createInsight({
                title: entry.title || entry.note.slice(0, 60) || 'Untitled insight',
                details: entry.note || null,
                project_id: activeProjectId,
                links,
              });
              return created.id;
            } catch (err) {
              console.error('Failed to sync insight to Insights page:', err);
              return entry.insightId ?? null;
            }
          };
          persist().then(insightId => {
            const saved = { ...entry, insightId };
            setDraft(d => {
              if (!d) return d;
              const existing = d.insights ?? [];
              if (editingInsight) {
                return { ...d, insights: existing.map(i => i.id === entry.id ? saved : i) };
              }
              return { ...d, insights: [...existing, saved] };
            });
          });
          setAddInsightOpen(false);
          setEditingInsight(null);
        }}
      />
    )}

    {releaseNoteOpen && (
      <ReleaseNoteModal
        editing={editingReleaseNote}
        onClose={() => { setReleaseNoteOpen(false); setEditingReleaseNote(null); }}
        onSave={(entry) => {
          setDraft(d => {
            if (!d) return d;
            const existing = d.releaseNoteEntries ?? [];
            if (editingReleaseNote) {
              return { ...d, releaseNoteEntries: existing.map(r => r.id === entry.id ? entry : r) };
            }
            return { ...d, releaseNoteEntries: [...existing, entry] };
          });
          setReleaseNoteOpen(false);
          setEditingReleaseNote(null);
        }}
      />
    )}

    {confirmDelete && (
      <div className="prm-confirm-backdrop" onClick={() => setConfirmDelete(false)}>
        <div className="prm-confirm-card" onClick={e => e.stopPropagation()}>
          <div className="prm-confirm-icon">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
          </div>
          <h3 className="prm-confirm-title">Delete this {kindNoun}?</h3>
          <p className="prm-confirm-text">
            <strong>{item.name}</strong> will be permanently removed. This action can’t be undone.
          </p>
          <div className="prm-confirm-actions">
            <button type="button" className="prm-confirm-cancel" onClick={() => setConfirmDelete(false)}>Cancel</button>
            <button
              type="button"
              className="prm-confirm-delete"
              onClick={() => { setConfirmDelete(false); onDelete?.(item.id); }}
            >
              Delete {kindNoun}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

/* ── Health update sub-modal (the small popup from the 2nd image) ── */
const HEALTH_STATUS_CONFIG: { value: HealthState; label: string; icon: string }[] = [
  { value: 'on-track', label: 'On track', icon: 'up' },
  { value: 'at-risk',  label: 'At risk',  icon: 'wave' },
  { value: 'off-track',label: 'Off track',icon: 'down' },
];

const HealthUpdateModal: React.FC<{
  item: PrioritizeItem | null;
  editing?: HealthEntry | null;
  onClose: () => void;
  onSave: (entry: HealthEntry) => void;
}> = ({ editing, onClose, onSave }) => {
  const statusConfig = HEALTH_STATUS_CONFIG;
  const [status, setStatus] = useState<HealthState>(
    editing ? (statusConfig.find(s => s.icon === editing.status)?.value ?? 'on-track') : 'on-track'
  );
  const [note, setNote] = useState(editing?.note ?? '');

  const handlePost = () => {
    const cfg = statusConfig.find(s => s.value === status)!;
    onSave({
      status: cfg.icon,
      label: cfg.label,
      note: note.trim() || undefined,
      by: editing?.by ?? 'You',
      at: editing?.at ?? new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    });
  };

  return (
    <div className="cum-modal-backdrop" onClick={onClose}>
      <div
        className="inline-edit-form premium-task-modal cum-company-modal prm-submodal health-update-modal"
        onClick={e => e.stopPropagation()}
      >
        <div className="task-modal-header">
          <div className="header-titles-new">
            <h3>Update health</h3>
            <p className="header-subtitle-new">Post a status update for this feature</p>
          </div>
          <button type="button" className="task-modal-close" onClick={onClose} title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="health-update-body">
          {/* Status bar (3 segments) */}
          <div className="health-update-status">
            {statusConfig.map(s => (
              <button
                key={s.value}
                type="button"
                className={`health-update-status-btn ${status === s.value ? 'health-update-status-btn-active' : ''}`}
                onClick={() => setStatus(s.value)}
              >
                <span className={`health-update-status-icon health-update-status-icon-${s.value}`}>
                  {s.icon === 'up' && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="3 17 9 11 13 15 21 7"/></svg>}
                  {s.icon === 'wave' && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M2 12c2-3 4-3 6 0s4 3 6 0 4-3 6 0 4 3 6 0"/></svg>}
                  {s.icon === 'down' && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="3 7 9 13 13 9 21 17"/></svg>}
                </span>
                <span className="health-update-status-label">{s.label}</span>
              </button>
            ))}
          </div>

          {/* Note textarea */}
          <div className="health-update-note-wrap">
            <textarea
              className="health-update-note"
              placeholder="Write your own health update or save time with a template."
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={5}
            />
            <div className="health-update-toolbar">
              <button type="button" className="health-update-tool-btn" title="Toggle header"><strong>H</strong></button>
              <button type="button" className="health-update-tool-btn" title="Bold"><strong>B</strong></button>
              <button type="button" className="health-update-tool-btn" title="Italic"><em>I</em></button>
              <button type="button" className="health-update-tool-btn" title="List">•</button>
              <button type="button" className="health-update-tool-btn" title="Numbered list">1.</button>
            </div>
          </div>

          {/* Post button */}
          <div className="health-update-actions">
            <button type="button" className="health-update-post-btn" onClick={handlePost}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              Post update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Add Insight sub-modal (matches the reference image) ──── */
const AddInsightModal: React.FC<{
  members: OrgMember[];
  editing?: InsightEntry | null;
  onClose: () => void;
  onSave: (entry: InsightEntry) => void;
}> = ({ members, editing, onClose, onSave }) => {
  const IMPORTANCE_TITLES = ['Low', 'Medium', 'High', 'Critical'];
  const [customer, setCustomer] = useState<string | null>(editing && editing.companyName !== 'Unassigned' ? editing.companyName : null);
  const [importance, setImportance] = useState<ValueGauge | null>(editing?.importance ?? null);
  const [title, setTitle] = useState(editing?.title ?? '');
  const [note, setNote] = useState(editing?.note ?? '');
  const [custOpen, setCustOpen] = useState(false);
  const [custQuery, setCustQuery] = useState('');
  const custRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (custRef.current && !custRef.current.contains(e.target as Node)) setCustOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const custMatches = members.filter(m => {
    const q = custQuery.trim().toLowerCase();
    if (!q) return true;
    return (m.full_name ?? '').toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q);
  }).slice(0, 8);
  const canSave = !!title.trim();

  const save = () => {
    if (!canSave) return;
    onSave({
      id: editing?.id ?? Math.random().toString(36).slice(2, 10),
      insightId: editing?.insightId ?? null,
      title: title.trim(),
      companyName: customer || 'Unassigned',
      noteType: importance ? IMPORTANCE_TITLES[importance - 1] : 'General',
      importance,
      note: note.trim(),
      createdAt: editing?.createdAt ?? new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    });
  };

  return (
    <div className="cum-modal-backdrop" onClick={onClose}>
      <div className="inline-edit-form premium-task-modal cum-company-modal prm-submodal" onClick={e => e.stopPropagation()}>
        <div className="task-modal-header">
          <div className="header-titles-new"><h3>{editing ? 'Edit insight' : 'New insight'}</h3></div>
          <button type="button" className="task-modal-close" onClick={onClose} title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div className="prm-submodal-body">
          <div className="prm-submodal-row">
            <div className="prm-submodal-field">
              <label className="prm-submodal-label">Customer</label>
              {/* Searchable picker — identical pattern to the Insights page Add-Insight customer field */}
              <div style={{ position: 'relative' }} ref={custRef}>
                {customer ? (
                  <div className="ism-customer-card" onClick={() => { setCustOpen(p => !p); setCustQuery(''); }}>
                    <span className="ism-customer-avatar">{customer.slice(0, 2).toUpperCase()}</span>
                    <div className="ism-customer-info">
                      <span className="ism-customer-name">{customer}</span>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="sidebar-select-trigger-new has-icon-new cum-owner-trigger" onClick={() => { setCustOpen(p => !p); setCustQuery(''); }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8' }}>
                      <span className="cum-owner-circle" />Select company or user
                    </span>
                    <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                )}
                {custOpen && (
                  <div className="cum-owner-dropdown">
                    <div style={{ padding: '6px 8px 4px' }}>
                      <input
                        className="task-title-input-new cum-owner-input"
                        type="text"
                        placeholder="Search users…"
                        value={custQuery}
                        onChange={e => setCustQuery(e.target.value)}
                        autoFocus
                        style={{ fontSize: 13, fontWeight: 400 }}
                      />
                    </div>
                    <button type="button" className="cum-owner-option" onClick={() => { setCustomer(null); setCustOpen(false); }}>
                      <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No customer</span>
                    </button>
                    {custMatches.map(m => {
                      const name = m.full_name || m.email || 'Unnamed';
                      return (
                        <button key={m.user_id} type="button" className="cum-owner-option" onClick={() => { setCustomer(name); setCustOpen(false); }}>
                          <span className="cum-owner-avatar cum-owner-avatar-sm">{name.slice(0, 2).toUpperCase()}</span>
                          <div className="cum-owner-info">
                            <span className="cum-owner-name">{name}</span>
                            {m.email && <span className="cum-owner-email">{m.email}</span>}
                          </div>
                        </button>
                      );
                    })}
                    {custMatches.length === 0 && <div className="cum-owner-empty">No users found</div>}
                  </div>
                )}
              </div>
            </div>

            <div className="prm-submodal-field">
              <label className="prm-submodal-label">Importance for this feature</label>
              <span className="prm-gauge prm-gauge-4">
                {[1, 2, 3, 4].map(i => (
                  <span
                    key={i}
                    className={`prm-gauge-box ${importance && i <= importance ? `prm-gauge-box-fill prm-gauge-box-fill-${i}` : ''}`}
                    onClick={() => setImportance(importance === i ? null : (i as ValueGauge))}
                    title={IMPORTANCE_TITLES[i - 1]}
                  />
                ))}
              </span>
            </div>
          </div>

          <div className="prm-submodal-field">
            <label className="prm-submodal-label">Title</label>
            <input
              className="prm-submodal-input"
              type="text"
              placeholder="Insight title…"
              value={title}
              onChange={e => setTitle(e.target.value)}
              autoFocus
            />
          </div>

          <div className="prm-submodal-field">
            <label className="prm-submodal-label">Description</label>
            <textarea
              className="prm-submodal-textarea"
              placeholder="Write down insight details..."
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={6}
            />
          </div>
        </div>

        <div className="task-modal-footer">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="submit-btn" onClick={save} disabled={!canSave}>{editing ? 'Save changes' : 'Add insight'}</button>
        </div>
      </div>
    </div>
  );
};

/* ── Release Note sub-modal (mirrors the Health Update modal) ── */
const RELEASE_NOTE_STATUS: { value: string; label: string; color: string }[] = [
  { value: 'planned',   label: 'Planned',   color: '#3B82F6' },
  { value: 'shipped',   label: 'Shipped',   color: '#22C55E' },
  { value: 'delayed',   label: 'Delayed',   color: '#F97316' },
];

const ReleaseNoteModal: React.FC<{
  editing?: ReleaseNoteEntry | null;
  onClose: () => void;
  onSave: (entry: ReleaseNoteEntry) => void;
}> = ({ editing, onClose, onSave }) => {
  const [status, setStatus] = useState(editing?.status ?? 'planned');
  const [note, setNote] = useState(editing?.note ?? '');

  const handlePost = () => {
    const cfg = RELEASE_NOTE_STATUS.find(s => s.value === status)!;
    onSave({
      id: editing?.id ?? Math.random().toString(36).slice(2, 10),
      status: cfg.value,
      label: cfg.label,
      note: note.trim(),
      by: editing?.by ?? 'You',
      at: editing?.at ?? new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    });
  };

  return (
    <div className="cum-modal-backdrop" onClick={onClose}>
      <div className="inline-edit-form premium-task-modal cum-company-modal prm-submodal" onClick={e => e.stopPropagation()}>
        <div className="task-modal-header">
          <div className="header-titles-new">
            <h3>{editing ? 'Edit release note' : 'New release note'}</h3>
            <p className="header-subtitle-new">Record what's shipping for this feature</p>
          </div>
          <button type="button" className="task-modal-close" onClick={onClose} title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div className="health-update-body">
          <div className="health-update-status">
            {RELEASE_NOTE_STATUS.map(s => (
              <button
                key={s.value}
                type="button"
                className={`health-update-status-btn ${status === s.value ? 'health-update-status-btn-active' : ''}`}
                onClick={() => setStatus(s.value)}
              >
                <span className="health-update-status-icon" style={{ background: s.color }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </span>
                <span className="health-update-status-label">{s.label}</span>
              </button>
            ))}
          </div>

          <div className="health-update-note-wrap">
            <textarea
              className="health-update-note"
              placeholder="Write the release note — what's shipping and why it matters…"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={5}
            />
          </div>

          <div className="health-update-actions">
            <button type="button" className="health-update-post-btn" onClick={handlePost} disabled={!note.trim()}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              {editing ? 'Save changes' : 'Add release note'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Inline sub-components used by the field rows ────────── */
const ParentPicker: React.FC<{
  candidates: PrioritizeItem[];
  selectedId?: string | null;
  onChange: (id: string | null) => void;
  required?: boolean;
}> = ({ candidates, selectedId, onChange, required }) => {
  const [open, setOpen] = useState(false);
  const { wrapRef, dropUp } = useDropUp(open);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, wrapRef]);
  const selected = candidates.find(c => c.id === selectedId);
  return (
    <div className="prm-input-wrap" ref={wrapRef}>
      <button type="button" className="prm-trigger" onClick={() => setOpen(o => !o)}>
        {selected ? (
          <span className="prm-parent-pill">
            {selected.kind === 'release-group'
              ? <ReleaseGroupSymbol />
              : selected.kind === 'product'
                ? <ProductSymbol size={12} color={selected.color || '#3B82F6'} />
                : <ComponentSymbol size={12} color={selected.color || '#8B5CF6'} />}
            <span>{selected.name}</span>
          </span>
        ) : (
          <span className="prm-trigger-placeholder">
            {required ? 'Select a parent' : 'None'}
          </span>
        )}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className={`prm-dropdown ${dropUp ? 'prm-dropdown-up' : ''}`}>
          {candidates.length === 0 && <div className="prm-dropdown-empty">No options</div>}
          {candidates.map(c => (
            <button
              key={c.id}
              type="button"
              className={`prm-dropdown-item ${c.id === selectedId ? 'prm-dropdown-item-selected' : ''}`}
              onClick={() => { onChange(c.id); setOpen(false); }}
            >
              {c.kind === 'release-group'
                ? <ReleaseGroupSymbol />
                : c.kind === 'product'
                  ? <ProductSymbol size={12} color={c.color || '#3B82F6'} />
                  : <ComponentSymbol size={12} color={c.color || '#8B5CF6'} />}
              <span>{c.name}</span>
            </button>
          ))}
          {!required && selected && (
            <>
              <div className="prm-dropdown-divider" />
              <button
                type="button"
                className="prm-dropdown-item prm-dropdown-item-muted"
                onClick={() => { onChange(null); setOpen(false); }}
              >
                <span>Clear</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* Member picker — replicates the Insight assignee drawer exactly:
   the trigger swaps to a search input with an avatar/name/email
   dropdown (cum-owner-* classes from CustomerModal). */
const OwnerPicker: React.FC<{
  members: OrgMember[];
  selectedId?: string | null;
  onChange: (id: string | null) => void;
}> = ({ members, selectedId, onChange }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { wrapRef, dropUp } = useDropUp(showSearch);
  useEffect(() => {
    if (!showSearch) return;
    const h = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setShowSearch(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showSearch, wrapRef]);

  const member = members.find(m => m.user_id === selectedId);
  const matches = members.filter(m => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (m.full_name ?? '').toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q);
  }).slice(0, 8);

  return (
    <div style={{ position: 'relative', width: '100%' }} ref={wrapRef}>
      {showSearch ? (
        <div className="cum-owner-search-box">
          <input
            ref={inputRef}
            className="task-title-input-new cum-owner-input"
            type="text"
            placeholder="Search members…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoFocus
          />
          <div className={`cum-owner-dropdown ${dropUp ? 'cum-owner-dropdown-up' : ''}`}>
            <button type="button" className="cum-owner-option" onClick={() => { onChange(null); setQuery(''); setShowSearch(false); }}>
              <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No assignee</span>
            </button>
            {matches.length === 0 && query.trim() && <div className="cum-owner-empty">No members found</div>}
            {matches.map(m => (
              <button key={m.user_id} type="button" className="cum-owner-option" onClick={() => { onChange(m.user_id); setQuery(''); setShowSearch(false); }}>
                <span className="cum-owner-avatar">{(m.full_name || m.email || '?')[0].toUpperCase()}</span>
                <div className="cum-owner-info">
                  <span className="cum-owner-name">{m.full_name || 'Unnamed'}</span>
                  <span className="cum-owner-email">{m.email}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <button type="button" className="sidebar-select-trigger-new has-icon-new cum-owner-trigger" onClick={() => setShowSearch(true)}>
          {member ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="cum-owner-avatar cum-owner-avatar-sm">{(member.full_name || member.email || '?')[0].toUpperCase()}</span>
              <span>{member.full_name || member.email}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="cum-owner-circle" />
              <span style={{ color: '#94a3b8' }}>Not assigned</span>
            </div>
          )}
          <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
      )}
    </div>
  );
};

const StatusSelect: React.FC<{
  status?: FeatureStatus | null;
  onChange: (s: FeatureStatus | null) => void;
}> = ({ status, onChange }) => {
  const [open, setOpen] = useState(false);
  const { wrapRef, dropUp } = useDropUp(open);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, wrapRef]);
  const opts: { value: FeatureStatus; label: string }[] = [
    { value: 'new-idea',    label: 'New idea' },
    { value: 'candidate',   label: 'Candidate' },
    { value: 'planned',     label: 'Planned' },
    { value: 'in-progress', label: 'In progress' },
    { value: 'released',    label: 'Released' },
    { value: 'backlog',     label: 'Backlog' },
    { value: 'done',        label: 'Done' },
  ];
  return (
    <div className="prm-input-wrap" ref={wrapRef}>
      <button type="button" className="prm-trigger" onClick={() => setOpen(o => !o)}>
        {status ? <StatusValue status={status} /> : <span className="prm-trigger-placeholder">Set status</span>}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className={`prm-dropdown ${dropUp ? 'prm-dropdown-up' : ''}`}>
          {opts.map(o => (
            <button
              key={o.value}
              type="button"
              className="prm-dropdown-item"
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              <StatusValue status={o.value} />
            </button>
          ))}
          <div className="prm-dropdown-divider" />
          <button type="button" className="prm-dropdown-item prm-dropdown-item-muted" onClick={() => { onChange(null); setOpen(false); }}>
            <span>Clear</span>
          </button>
        </div>
      )}
    </div>
  );
};

/* Release status — 3 options with colored flag icons (per the design). */
const ReleaseFlag: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={color} stroke="none" style={{ flexShrink: 0 }}><path d="M5 3v18l7-5 7 5V3z"/></svg>
);
const ReleaseStatusSelect: React.FC<{
  status?: FeatureStatus | null;
  onChange: (s: FeatureStatus | null) => void;
}> = ({ status, onChange }) => {
  const [open, setOpen] = useState(false);
  const { wrapRef, dropUp } = useDropUp(open);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, wrapRef]);
  const sel = RELEASE_STATUS_OPTIONS.find(o => o.value === status);
  return (
    <div className="prm-input-wrap" ref={wrapRef}>
      <button type="button" className="prm-trigger" onClick={() => setOpen(o => !o)}>
        {sel ? (
          <span className="prm-selectbox-val"><ReleaseFlag color={sel.color} /><span>{sel.label}</span></span>
        ) : <span className="prm-trigger-placeholder">Set status</span>}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className={`prm-dropdown ${dropUp ? 'prm-dropdown-up' : ''}`}>
          {RELEASE_STATUS_OPTIONS.map(o => (
            <button key={o.value} type="button" className={`prm-dropdown-item ${o.value === status ? 'prm-dropdown-item-selected' : ''}`} onClick={() => { onChange(o.value); setOpen(false); }}>
              <ReleaseFlag color={o.color} /><span>{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Teams picker (searchable multi-select) ──────────────────
   TODO: replace DUMMY_TEAMS with a real teams data source when the
   teams backend is built. Selection is stored as a comma-joined
   string on `draft.teams`. */
const DUMMY_TEAMS = [
  'Engineering Team',
  'Design Team',
  'Product Team',
  'Marketing Team',
  'Sales Team',
  'Support Team',
];

const teamInitials = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const TeamsPicker: React.FC<{
  value?: string | null;
  onChange: (v: string | null) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { wrapRef, dropUp } = useDropUp(open);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, wrapRef]);

  const selected = (value ?? '').split(',').map(s => s.trim()).filter(Boolean);
  const matches = DUMMY_TEAMS.filter(t => !query.trim() || t.toLowerCase().includes(query.trim().toLowerCase()));

  const toggle = (team: string) => {
    const next = selected.includes(team)
      ? selected.filter(t => t !== team)
      : [...selected, team];
    onChange(next.length ? next.join(', ') : null);
  };

  return (
    <div className="prm-input-wrap" ref={wrapRef}>
      <button type="button" className="prm-trigger" onClick={() => { setOpen(o => !o); setQuery(''); }}>
        {selected.length > 0 ? (
          <span className="prm-team-trigger-val">
            <span className="prm-team-avatar">{teamInitials(selected[0])}</span>
            <span>{selected.length === 1 ? selected[0] : `${selected[0]} +${selected.length - 1}`}</span>
          </span>
        ) : (
          <span className="prm-trigger-placeholder">Not assigned</span>
        )}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className={`prm-dropdown prm-dropdown-search ${dropUp ? 'prm-dropdown-up' : ''}`}>
          <div className="prm-dropdown-searchbar">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              autoFocus
              className="prm-dropdown-search-input"
              placeholder="Search teams…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <div className="prm-dropdown-scroll">
            {matches.length === 0 && <div className="prm-dropdown-empty">No teams found</div>}
            {matches.map(team => {
              const checked = selected.includes(team);
              return (
                <button key={team} type="button" className="prm-dropdown-item prm-team-option" onClick={() => toggle(team)}>
                  <span className={`prm-team-check ${checked ? 'prm-team-check-on' : ''}`}>
                    {checked && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    )}
                  </span>
                  <span className="prm-team-avatar">{teamInitials(team)}</span>
                  <span className="prm-team-name">{team}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Tags input — inline wrapping chips, identical to InsightModal's
   sidebar Tags (chips + "+ Tag" that reveals an inline input). ──── */
const TagsInput: React.FC<{
  value?: string | null;
  onChange: (v: string | null) => void;
}> = ({ value, onChange }) => {
  const [showInput, setShowInput] = useState(false);
  const [input, setInput] = useState('');

  const tags = (value ?? '').split(',').map(s => s.trim()).filter(Boolean);
  const addTag = () => {
    const val = input.trim();
    if (val && !tags.includes(val)) onChange([...tags, val].join(', '));
    setInput('');
    setShowInput(false);
  };
  const removeTag = (t: string) => {
    const next = tags.filter(x => x !== t);
    onChange(next.length ? next.join(', ') : null);
  };

  return (
    <div className="ism-chip-row">
      {tags.map(tag => (
        <span key={tag} className="ism-chip">
          {tag}
          <button type="button" className="ism-chip-remove" onClick={() => removeTag(tag)}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </span>
      ))}
      {showInput ? (
        <input
          className="ism-chip-input"
          autoFocus
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); addTag(); }
            if (e.key === 'Escape') { setInput(''); setShowInput(false); }
          }}
          onBlur={addTag}
          placeholder="Tag name"
        />
      ) : (
        <button type="button" className="ism-add-chip-btn" onClick={() => setShowInput(true)}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Tag
        </button>
      )}
    </div>
  );
};

/* ── Timeframe field (status-height trigger + dual date popover) ── */
const TimeframeField: React.FC<{
  start?: string;
  end?: string;
  onChange: (start: string, end: string) => void;
}> = ({ start, end, onChange }) => {
  const [open, setOpen] = useState(false);
  const { wrapRef, dropUp } = useDropUp(open);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, wrapRef]);

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const pad = (n: number) => String(n).padStart(2, '0');
  const lastDay = (y: number, mZeroBased: number) => new Date(y, mZeroBased + 1, 0).getDate();

  // Derive the high-level view (year + quarter / month) from the stored range.
  const startD = start ? new Date(start + 'T00:00:00') : null;
  const endD = end ? new Date(end + 'T00:00:00') : null;
  const baseYear = startD && !isNaN(startD.getTime()) ? startD.getFullYear() : new Date().getFullYear();
  const [hlYear, setHlYear] = useState(baseYear);
  const [tab, setTab] = useState<'high-level' | 'specific'>('high-level');
  const [monthAnchor, setMonthAnchor] = useState(0); // first month shown in the months strip (0 or 6)

  // Which quarter is currently represented (full-quarter range), if any.
  const selectedQuarter = (() => {
    if (!startD || !endD || isNaN(startD.getTime()) || isNaN(endD.getTime())) return null;
    if (startD.getFullYear() !== endD.getFullYear()) return null;
    const sm = startD.getMonth(), em = endD.getMonth();
    if (startD.getDate() !== 1) return null;
    for (let qi = 0; qi < 4; qi++) {
      const qs = qi * 3, qe = qs + 2;
      if (sm === qs && em === qe && endD.getDate() === lastDay(endD.getFullYear(), qe)) return qi;
    }
    return null;
  })();
  // Which single month is represented (full-month range), if any.
  const selectedMonth = (() => {
    if (!startD || !endD || isNaN(startD.getTime()) || isNaN(endD.getTime())) return null;
    if (startD.getFullYear() !== endD.getFullYear() || startD.getMonth() !== endD.getMonth()) return null;
    if (startD.getDate() !== 1 || endD.getDate() !== lastDay(endD.getFullYear(), endD.getMonth())) return null;
    return startD.getMonth();
  })();

  const setQuarter = (y: number, qi: number) => {
    const qs = qi * 3, qe = qs + 2;
    onChange(`${y}-${pad(qs + 1)}-01`, `${y}-${pad(qe + 1)}-${pad(lastDay(y, qe))}`);
  };
  const setMonth = (y: number, m: number) => {
    onChange(`${y}-${pad(m + 1)}-01`, `${y}-${pad(m + 1)}-${pad(lastDay(y, m))}`);
  };

  const fmt = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  // Prefer a compact high-level label (Q1, 2025 / Mar 2025) when the range maps to one.
  const label = (() => {
    if (selectedQuarter != null && startD) return `Q${selectedQuarter + 1}, ${startD.getFullYear()}`;
    if (selectedMonth != null && startD) return `${MONTHS[selectedMonth]} ${startD.getFullYear()}`;
    if (start || end) return `${fmt(start) || '—'} – ${fmt(end) || '—'}`;
    return '';
  })();

  const monthsShown = [0, 1, 2, 3, 4, 5].map(i => monthAnchor + i).filter(m => m < 12);

  return (
    <div className="prm-input-wrap" ref={wrapRef}>
      <button type="button" className="prm-trigger" onClick={() => setOpen(o => !o)}>
        {label ? (
          <span className="prm-timeframe-val">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span>{label}</span>
          </span>
        ) : (
          <span className="prm-trigger-placeholder">Not assigned</span>
        )}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className={`prm-dropdown prm-tf-popover ${dropUp ? 'prm-dropdown-up' : ''}`} onClick={e => e.stopPropagation()}>
          {/* Tab switch */}
          <div className="prm-tf-tabs">
            <button type="button" className={`prm-tf-tab ${tab === 'high-level' ? 'active' : ''}`} onClick={() => setTab('high-level')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="3 3"><circle cx="12" cy="12" r="9"/></svg>
              High-level
            </button>
            <button type="button" className={`prm-tf-tab ${tab === 'specific' ? 'active' : ''}`} onClick={() => setTab('specific')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              Specific
            </button>
          </div>

          {tab === 'high-level' ? (
            <>
              {/* Years */}
              <div className="prm-tf-section-label">Years</div>
              <div className="prm-tf-stepper">
                <button type="button" className="prm-tf-arrow" onClick={() => setHlYear(y => y - 2)} aria-label="Previous years">‹</button>
                {[hlYear, hlYear + 1].map(y => (
                  <button key={y} type="button" className={`prm-tf-cell prm-tf-year ${startD?.getFullYear() === y ? 'active' : ''}`} onClick={() => setHlYear(y)}>{y}</button>
                ))}
                <button type="button" className="prm-tf-arrow" onClick={() => setHlYear(y => y + 2)} aria-label="Next years">›</button>
              </div>

              {/* Quarters */}
              <div className="prm-tf-section-label">Quarters</div>
              <div className="prm-tf-subyear">{hlYear}</div>
              <div className="prm-tf-stepper">
                <button type="button" className="prm-tf-arrow" disabled aria-hidden>‹</button>
                {[0, 1, 2, 3].map(qi => (
                  <button key={qi} type="button" className={`prm-tf-cell ${selectedQuarter === qi && startD?.getFullYear() === hlYear ? 'prm-tf-cell-solid' : ''}`} onClick={() => setQuarter(hlYear, qi)}>Q{qi + 1}</button>
                ))}
                <button type="button" className="prm-tf-arrow" disabled aria-hidden>›</button>
              </div>

              {/* Months */}
              <div className="prm-tf-section-label">Months</div>
              <div className="prm-tf-subyear">{hlYear}</div>
              <div className="prm-tf-stepper">
                <button type="button" className="prm-tf-arrow" onClick={() => setMonthAnchor(a => (a === 0 ? 6 : 0))} aria-label="Previous months">‹</button>
                {monthsShown.map(m => (
                  <button key={m} type="button" className={`prm-tf-cell prm-tf-month ${selectedMonth === m && startD?.getFullYear() === hlYear ? 'active' : ''}`} onClick={() => setMonth(hlYear, m)}>{MONTHS[m]}</button>
                ))}
                <button type="button" className="prm-tf-arrow" onClick={() => setMonthAnchor(a => (a === 0 ? 6 : 0))} aria-label="Next months">›</button>
              </div>
            </>
          ) : (
            <div className="prm-tf-specific">
              <label className="prm-date-field">
                <span className="prm-date-field-label">Start date</span>
                <input type="date" className="prm-date-input" value={start || ''} onChange={e => onChange(e.target.value, end || '')} />
              </label>
              <label className="prm-date-field">
                <span className="prm-date-field-label">End date</span>
                <input type="date" className="prm-date-input" value={end || ''} min={start || undefined} onChange={e => onChange(start || '', e.target.value)} />
              </label>
            </div>
          )}

          {/* Footer */}
          <div className="prm-tf-footer">
            <button type="button" className="prm-tf-clear" onClick={() => onChange('', '')}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/></svg>
              Clear
            </button>
            <button type="button" className="prm-tf-done" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrioritizeDetailModal;
